import { ComplianceAudioBadge } from "@/contexts/ComplianceAudioBadge";
import { characterService, DH_SOURCE_SLOT } from "@/entities/character/api/characterService";
import { SKU } from "@/entities/pricing/model/priceBook";
import { acquireQuoteId, useQuotePreview } from "@/entities/pricing/model/useQuote";
import { usePriceBook } from "@/entities/pricing/model/usePriceBook";
import { Loader2, Pause, Play, Sparkles, UserSquare2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { toast } from "@/shared/hooks/use-toast";
import {
  clearSceneCardState,
  parseScenePreset,
  sceneKeyOf,
} from "@/entities/scene-card/model/presets";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Label } from "@/shared/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { Switch } from "@/shared/ui/switch";
import { Textarea } from "@/shared/ui/textarea";
import { MediaThumb } from "@/entities/media-asset/ui/MediaThumb";
import {
  DH_LINE_ROUTE,
  type DhLineKey,
  parseDhLineSwitchState,
} from "@/entities/dh-profile/model/dhLines";
import { ToastAction } from "@/shared/ui/toast";
// 声音系统 V2 词表(语言/方言/演绎方式)。放 entities/dh-profile:陪练场景表单同词表,
// features 互不 import(ADR-0013),共用只能下沉。
import {
  DH_DEFAULT_VOICE_LANGUAGE,
  DH_DEFAULT_VOICE_STYLE,
  DH_FEATURED_DIALECTS,
  DH_VOICE_DIALECTS,
  DH_VOICE_LANGUAGES,
  DH_VOICE_STYLES,
  dialectLabelOf,
  isKnownVoiceDialect,
  isKnownVoiceLanguage,
  isKnownVoiceStyle,
  LEGACY_PROFILE_LOCK_TEXT,
  PRESET_VOICE_LOCK_TEXT,
  VOICE_STYLE_TRADEOFF_TEXT,
  voiceStyleLabelOf,
} from "@/entities/dh-profile/model/voiceV2Options";
import {
  voiceProfileDateLabel,
  voiceProfileSupportsTuning,
} from "@/entities/dh-profile/api/dhProfileService";
import { LegacyVoiceBadge } from "@/entities/dh-profile/ui/LegacyVoiceBadge";
// 1019:照片省流版永久下架 —— 老草稿 / 再来一条 / 卡片预填里的 longform 统一回落生动版。
import { isDhPhotoMode, normalizeDhPhotoMode } from "@/entities/dh-profile/model/dhLongform";
import { pauseOtherAudio } from "@/shared/lib/exclusiveAudio";
import { RecentWorksRow } from "@/entities/work/ui/RecentWorksRow";
import type { WorkItem } from "@/entities/work/model/types";
import { type CharacterPick } from "@/entities/character/ui/CharacterPickerDialog";
import { digitalHumanService, type DhMode, type DhScriptLine } from "../api/digitalHumanService";
import { type DhShowcaseRecipe } from "../api/dhShowcaseService";
import {
  parseDhExplosiveState,
  transcriptTextOf,
  type DhExplosiveHandoff,
} from "../model/dhBlueprint";
// 2026-10-10 追爆原话复用:C1 逐句分析(entities 层,直连文件不走 barrel —— 防撞整包 mock)
import { blueprintLinesService } from "@/entities/explosive/api/blueprintLinesService";
import {
  applyPreset,
  autoSwapForProduct,
  composeScript,
  hasProductLine,
  isFlagged,
  isPending,
  linesForBroll,
  mergeRewrite,
  rewriteRequestLines,
  rowsFromLines,
  rowText,
  setRowMode,
  unconfirmedFlagged,
  type DhLineMode,
  type DhLinePreset,
  type DhLineRow,
} from "../model/dhBlueprintLines";
import { DhBlueprintLinesEditor } from "./components/DhBlueprintLinesEditor";
import { filmTypeOfMode, parseDhRemakeState } from "../model/dhRemake";
import { dhVariantOf, estimateDhCost } from "../model/dhCostEstimate";
import { formatPerSecondRate, useDhPerSecondRate } from "../model/useDhPerSecondRate";
import { DhPhotoRateCard } from "./components/DhPhotoRateCard";
import { useDhPriceVariants } from "../model/useDhPriceVariants";
import { languageZhName, looksUntranslated } from "../model/dhScriptTranslate";
import { dhDurationLabel, dhSourceDurationLabel, useDhVideoLimits } from "../model/dhVideoLimits";
import { VOICE_USAGE_LABEL, presentUsages } from "../model/dhAssetTaxonomy";
import {
  MAX_AVATAR_VIDEO_MB,
  avatarVideoDurationError,
  avatarVideoResolutionError,
  avatarVideoSizeError,
  readVideoMeta,
} from "@/entities/dh-profile/model/dhUploadLimits";
import { splitScriptLines } from "../model/dhScriptLines";
import { usePresetVoiceOptions } from "../model/usePresetVoiceOptions";
import { DhAvatarPicker } from "./components/DhAvatarPicker";
import { DhAdaptProductDialog } from "./components/DhAdaptProductDialog";
import type { FormulaPayload } from "@/entities/explosive";
import { HandoffPreview } from "@/entities/explosive";
import { ReferenceContentPanel } from "@/entities/explosive";
import { buildReferenceContent } from "@/entities/explosive";
// BPWB-1(2026-09-03):细条 DhBlueprintBar 退役,统一蓝图工作卡(与视频/海报同形制)
import { BlueprintHeroCard, parseBeats } from "@/entities/explosive";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui/alert-dialog";
import { DhCreateStatusBar } from "./components/DhCreateStatusBar";
import { VoiceRecordEnrollCard } from "./components/VoiceRecordEnrollCard";
import { BrollMaterialPicker, type BrollMaterial } from "./components/BrollMaterialPicker";
import { DhBatchDialog } from "./components/DhBatchDialog";
import { DhDraftBar } from "./components/DhDraftBar";
import { DhShowcaseStrip } from "./components/DhShowcaseStrip";
import { DhBgmPanel, type DhBgmTab } from "./components/DhBgmPanel";
// ★曲库走 @/entities/media-bgm,不是 @/features/video:features 之间禁止互相 import
// (ADR-0013 单向核心);分身自己的 DhBgmPanel 状态落在本页 useState 里
// (见 bgmId/customBgmObjectKey),不耦合视频线的 FormState。
import { bgmService, type BgmDto } from "@/entities/media-bgm/api/bgmService";
import {
  clearDhDraft,
  dhDraftKey,
  draftAgeLabel,
  loadDhDraft,
  readDraftIdentity,
  saveDhDraft,
  type DhDraft,
} from "../model/dhDraft";
import {
  runDhBatch,
  type DhBatchItem,
  type DhBatchResult,
  type DhBatchTarget,
} from "../model/dhBatchSubmit";
import {
  DH_DEFAULT_VOICE,
  DH_PERFORMANCE_STYLE_ENABLED,
  DH_PERFORMANCE_STYLE_OPTIONS,
  DH_RESOLUTION_OPTIONS,
  dhResolutionLabel,
  filmTypeCards,
  VIDEO_REVERSAL_HINT,
  FLAGSHIP_MAX_SEC,
  FLAGSHIP_SCRIPT_MAX,
  estSecondsFromChars,
} from "../model/dhCreateOptions";
import { ApiError } from "@/shared/api/httpClient";
import { errorToast, errorText } from "@/shared/lib/apiErrors";

// B5.5 文生语音文案上限(后端同口径;≈4.5 字/秒 → 2000 字 ≈7.4 分钟音频,按秒计费时长闸)。
const TTS_SCRIPT_MAX = 2000;

/**
 * 语速分档(2026-09-08 老板实测「设了 1.2 倍实际没快」)。原来是 0.5~2.0 连续滑杆,
 * 相邻 0.1 挡各占轨宽 1/15,想拖 1.2 松手落 1.1,而 1.1 只快 10% 几乎听不出。改成
 * 少而分得开的离散档:点得准、且「快/很快」比默认明显。值仍在后端合法域 [0.5, 2.0] 内。
 */
const SPEECH_RATE_STEPS = [
  { value: 0.9, label: "慢 0.9" },
  { value: 1.0, label: "正常 1.0" },
  { value: 1.2, label: "快 1.2" },
  { value: 1.5, label: "很快 1.5" },
] as const;

/** 就近落档(存量草稿/返稿可能带连续值,如 1.1)—— 高亮最近的一档,不改已存值直到用户点选。 */
function nearestSpeechStep(rate: number): number {
  let best: number = SPEECH_RATE_STEPS[0].value;
  for (const s of SPEECH_RATE_STEPS) {
    if (Math.abs(s.value - rate) < Math.abs(best - rate)) best = s.value;
  }
  return best;
}

/**
 * 用途 chip 的**选中**态。原来是 `border-primary bg-primary/5 text-primary` ——
 * 三皮肤实测 comic 下「全部」只有 2.35(要 4.5):浅粉主色当字色、又压在同色淡底上。
 * 改成与声音库页那排 chip 同一套(正文色 + 实底 + 阴影)—— 选中靠**底与影**区分,
 * 不靠一个本来就浅的颜色。
 */
const USAGE_CHIP_ON = "border-primary bg-background font-medium text-foreground shadow-sm";

/**
 * 「用谁的声音」墙**内置音色组**收起时露出的卡数(1006 起按组折叠:声纹组恒全展开,
 * 老板声纹最多十几条;内置组 2 列 × 3 行 = 6 张)。原注(C 批返工,2026-09-08):
 * 7 + 恒在末位的「+ 克隆我的声音」虚线卡 = 8 格,正好是 sm:grid-cols-4 的两行 ——
 * 预置 8 款 + 声纹会多,墙不设上限会把 ③ 卡撑成一整屏。仓内没有「N 行折叠」先例,
 * 按格数切片(格数确定、断言稳定)而不是 max-h 裁半张卡。
 */
const PRESET_WALL_VISIBLE = 6;

/**
 * chip 墙上的小圆试听钮:点一下播样音、再点暂停。
 * ★互斥走全站 pauseOtherAudio(0813「两个声音都在报读」的教训);没有 sampleUrl 的卡
 *   不渲染本组件 —— 不做点了不出声的假试听(PresetVoiceCatalog 立的规矩)。
 * ★独立成组件是为了每张卡各自持 audio ref/播放态;不能塞进卡片的选择按钮里
 *   (button 套 button 是非法 DOM),调用方把它绝对定位在卡片右上角。
 */
function VoiceSampleDot({ voiceId, label, src }: { voiceId: string; label: string; src: string }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  return (
    <>
      <button
        type="button"
        aria-label={`试听 ${label} 样音`}
        data-testid={`dh-voice-wall-sample-${voiceId}`}
        onClick={() => {
          const el = audioRef.current;
          if (!el) return;
          if (el.paused) void el.play().catch(() => {});
          else el.pause();
        }}
        className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-primary transition hover:bg-primary/20"
      >
        {playing ? <Pause className="h-2.5 w-2.5" /> : <Play className="ml-px h-2.5 w-2.5" />}
      </button>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        ref={audioRef}
        src={src}
        preload="none"
        onPlay={(e) => {
          pauseOtherAudio(e);
          setPlaying(true);
        }}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        className="hidden"
      />
    </>
  );
}

/**
 * 创作线(2026-08-07 IA 重构):一卡一页。
 *
 * ★三条线不是"同一条管线的三种画幅",而是**三条真·不同的管线**:引擎不同
 *   (videoretalk / liveportrait / cosyvoice)、参数集不同(视频档根本不吃 resolution)、
 *   产物不同(视频/视频/音频)、计费口径差 25 倍。所以片型不再是页面里的一个控件 ——
 *   它由**你点的是哪张卡**决定,进门不再问第二遍。
 */
export type DhLine = DhLineKey;

const LINE_FILM_TYPE: Record<DhLine, "video" | "photo" | "tts"> = {
  talking: "video",
  photo: "photo",
  tts: "tts",
};

// 路由表单源在 entities(入口散在三个 feature + 场景卡目录,features 互不 import)。
export { DH_LINE_ROUTE };

/** 页名 + 「换一种做法」里给的一句话(说清另一条线解决什么,不是罗列名字)。 */
export const DH_LINE_META: Record<DhLine, { title: string; switchHint: string }> = {
  talking: { title: "真人口播工作台", switchHint: "有你自己的出镜视频,换台词对口型" },
  photo: { title: "照片口播工作台", switchHint: "不用拍视频,一张正脸照就能开口" },
  tts: { title: "配音工作台", switchHint: "只要声音,不出画面" },
};

const PHOTO_TYPES = /^image\/(png|jpeg|webp)$/;
// 视频形象(videoretalk 源视频):后端只收 mp4(H264);100MB=prod nginx client_max_body_size 硬上限。
// ★0829 25→100MB 支持 2 分钟视频;真正的时长上限由 ai 侧按出片通道拦(1020 起 2 / 5 分钟,见 model/dhVideoLimits)。
const VIDEO_TYPES = /^video\/(mp4|quicktime)$/;
// 0829:收敛到单一源(此前 PC 形象库 / PC 创建页 / 企微 H5 各写了一份 25)。
const MAX_VIDEO_MB = MAX_AVATAR_VIDEO_MB;

/**
 * 数字人口播出片表单(A 阶段):上传单人正面照 + 肖像授权勾选 + 档位/分辨率/音色/文案 → createTask → 跳详情。
 * 租户关闭真人照片上传(dhAllowRealPhoto=false)时禁上传区并提示。
 */
export default function DigitalHumanCreatePage({ line }: { line: DhLine }) {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  // 样片墙「一键套用」经导航 state 传 recipe(全 preset 安全资产)→ 预填形象/音色/文案/分辨率。
  // 混合 state 优先级(codex 修):explosive 命中时整包忽略 recipe —— 两来源的脚本语义冲突,
  // 否则蓝图脚本生成返回前可用样片脚本提交出「recipe 文案 + 爆款 blueprintId」的错配档案。
  const recipe = parseDhExplosiveState(location.state)
    ? undefined
    : (location.state as { recipe?: DhShowcaseRecipe } | null)?.recipe;
  // R2-c 落地端:详情页「回工作台改这稿」带来的交接包。与 recipe/explosive 三选一
  //(三者都是「别人替你填好的一稿」,同时生效会混出错配档案 —— 存量已踩过)。
  // ★只回填后端真回了的字段;形象一律不预选,理由见详情页 onRemake 处的注释。
  const remake =
    recipe || parseDhExplosiveState(location.state) ? null : parseDhRemakeState(location.state);

  const settingsQuery = useQuery({
    queryKey: ["tenant/settings"],
    queryFn: () => digitalHumanService.getTenantGate(),
  });
  // 无值(加载中/老后端)兜底为允许;仅显式 false 才禁。
  const allowPhoto = settingsQuery.data?.dhAllowRealPhoto !== false;

  // 声纹档案(B1):切到「我的声纹」音色时选用;voiceRef=profileId。授权入库后全公司共用
  // (录音克隆须本人、跨公司隔离、删除仅本人或管理员)。
  const voicesQuery = useQuery({
    queryKey: ["dh", "voice-profiles"],
    queryFn: () => digitalHumanService.listVoiceProfiles(),
  });
  const voiceProfiles = voicesQuery.data?.profiles ?? [];

  // 预置形象库(B2,平台共享安全资产,免 consent)。
  const presetsQuery = useQuery({
    queryKey: ["dh", "preset-avatars"],
    queryFn: () => digitalHumanService.listPresetAvatars(),
  });
  const presetAvatars = presetsQuery.data ?? [];

  /*
   * ★「我的形象库」query 退役(0915 归一批 B):形象表并入素材表,素材属于人物,
   * 不再有"所有形象的扁平列表"这个集合。选一张已存的脸 = 选一位人物(picker 的
   * 「按人物选」),avatarRef 从此指 xb_pack_material.id。
   */
  const avatarProfiles: never[] = [];
  /**
   * 选中素材的回显(名字/缩图/介质)。
   *
   * ★归一前这些是去「我的形象」扁平列表里按 id 反查的;列表退役后没有可查的地方,
   * 改在选中那一刻就地记一份 —— 回显本来就只需要这三样,为它保留一个全量列表是浪费。
   */
  const [pickedMaterial, setPickedMaterial] = useState<{
    label: string;
    thumbUrl: string | null;
    kind: "image" | "video";
  } | null>(null);

  // 预置音色:与声音库页同一个后端真数据源(E4)。★此前本页用写死的 DH_VOICE_OPTIONS,
  // 而声音库页读 preset-voices —— 两边一漂,声音库里「用 TA 出片」带过来的 presetVoice
  // 在本页 Select 里没有对应 SelectItem,音色框直接渲染成空白(用户以为没选上)。
  // 2026-08-07 R3-a:这段就地逻辑抽成 usePresetVoiceOptions,详情页/超管上架/企微 H5 同源。
  const { options: voiceOptions, voices: presetVoiceDtos } = usePresetVoiceOptions();

  // ★片型 = 显式第 1 步(2026-07-15 重排)。旧设计里它是**选形象的副作用**
  // (isVideoAvatar = 选中形象.kind==="video"),用户从没「选择」过视频档,只看见选了视频形象后
  // 档位/清晰度凭空消失、勾用原声后音色也消失,5 控件塌成 1、零解释(ui-ux-pro-max:
  // Content Jumping / Disabled States)。照片档与视频档是能力/时长/成本不同的两条管线,
  // 差异必须在选片型那刻讲清 → 后面是「从没提供过」而非「凭空消失」。
  // 套用样片(recipe)带的是预置形象 = 照片资产 → 落照片档。
  // ★片型 = 这一页是哪条线,不是页面里的一个控件(2026-08-07 IA 重构)。
  const filmType = LINE_FILM_TYPE[line];
  /**
   * 「这条线的最近作品」筛子。`variant` = `media_task.mode`,`filmTypeOfMode` 把四档 mode
   * 折成三条线(flagship/longform 同属照片线)。★认不出的老 mode 归 talking —— 与跳板
   * `resolveDhLine` 的兜底同口径,不另立一套「归到哪」的规则。
   */
  const recentFilter = {
    key: `dh-line-${line}`,
    predicate: (w: WorkItem) => (filmTypeOfMode(w.variant ?? undefined) ?? "video") === filmType,
  };
  const isVideoAvatar = filmType === "video";
  // B5.5 文生语音(纯音频档):无形象,script+音色 TTS,产物=音频。
  const isTts = filmType === "tts";

  // 平台曲库(2026-09-07):与视频线共库,复用 bgmService.listBgms()(下沉在 entities,
  // 详见该文件头注)。只在出视频的档位拉(isTts 那条线产物是音频,配乐无意义,不必白打一次
  // 请求)。拉不到不阻塞出片 —— 只是配乐选择器里没有预置可选,与视频线「BGM 拉不到不阻塞」
  // 同一条规矩。
  const bgmsQuery = useQuery({
    queryKey: ["media-bgm", "list"],
    queryFn: () => bgmService.listBgms(),
    enabled: !isTts,
    retry: false,
    meta: { suppressGlobalErrorToast: true },
  });
  // ★防御式:测试里常见的通用 api mock(如「@/shared/api」全局 stub)可能对任何 GET 都回同
  //   一个形状(如任务分页壳 {records,total}),不是数组也不会是 undefined —— 光 `?? []`
  //   接不住这种情况,Select 一 `.map` 就炸。判 Array.isArray 才是真的兜底。
  const bgms: BgmDto[] = Array.isArray(bgmsQuery.data) ? bgmsQuery.data : [];

  // 照片档只剩生动版(1019 省流版永久下架);老「再来一条」带 longform 也回落到这里。
  const [mode, setMode] = useState<DhMode>(
    isDhPhotoMode(remake?.mode) ? normalizeDhPhotoMode(remake.mode) : "flagship"
  );
  const [resolution, setResolution] = useState(remake?.resolution ?? recipe?.resolution ?? "480P");
  // 表演风格(火山 §10):仅生动版生效;入口由 DH_PERFORMANCE_STYLE_ENABLED 闸(万相期间不放假交互)。
  const [performanceStyle, setPerformanceStyle] = useState(
    remake?.performanceStyle ?? "professional"
  );
  // 套用样片默认走「预置形象」(recipe.avatarId 是预置形象 id)。
  const [avatarKind, setAvatarKind] = useState<"upload" | "preset" | "profile">(
    recipe ? "preset" : "upload"
  );
  const [presetAvatarId, setPresetAvatarId] = useState(recipe?.avatarId ?? "");
  const [avatarProfileId, setAvatarProfileId] = useState("");
  /**
   * 已经有音色时,现场克隆卡默认收起(0813 琳琳「上传克隆过了,还提示再录一遍」)。
   * 一个都没有时这个标记不参与判断 —— 那种情况下卡本身就是主路,恒展开。
   */
  const [showEnrollCard, setShowEnrollCard] = useState(false);
  // 内置音色组超过 PRESET_WALL_VISIBLE 张时折叠,这是「展开全部」的开关(1006 起只管内置组)。
  const [voiceWallExpanded, setVoiceWallExpanded] = useState(false);
  const [voiceKind, setVoiceKind] = useState<"preset" | "profile">(
    // 交接包里的 voiceSource=profile 只说明「上一稿用的是我的声纹」;具体哪一条(voiceRef)
    // 后端没回 → 切到我的声纹 tab 让用户自己挑,不预选、更不编造一条。
    remake?.voiceSource === "profile" ? "profile" : "preset"
  );
  const [voice, setVoice] = useState(remake?.presetVoice ?? recipe?.voiceId ?? DH_DEFAULT_VOICE);
  const [profileId, setProfileId] = useState("");
  // ── 声音 V2 三件套(2026-09-08):语言/方言/演绎方式。只对「我的声纹」真实生效
  //    (克隆声线走新模型;预置音色是旧引擎,选预置时控件收起并明说)。返稿回填走词表
  //    校验:认不出的值退默认,不整包作废(词表会演进)。
  const [voiceLanguage, setVoiceLanguage] = useState<string>(() =>
    isKnownVoiceLanguage(remake?.voiceLanguage) ? remake!.voiceLanguage! : DH_DEFAULT_VOICE_LANGUAGE
  );
  // ★方言依附语言:契约「仅中文可非空」。返稿语言不是中文时方言一律落普通话位。
  const [voiceDialect, setVoiceDialect] = useState<string | null>(() =>
    (remake?.voiceLanguage == null || remake.voiceLanguage === "zh") &&
    isKnownVoiceDialect(remake?.voiceDialect)
      ? (remake!.voiceDialect as string)
      : null
  );
  const [voiceStyle, setVoiceStyle] = useState<string>(() =>
    isKnownVoiceStyle(remake?.voiceStyle) ? remake!.voiceStyle! : DH_DEFAULT_VOICE_STYLE
  );
  /** 换语言必须顺手收方言(方言是中文专属;残留一个 yueyu 配 en 会被后端拦)。 */
  const onChangeVoiceLanguage = (v: string) => {
    setVoiceLanguage(v);
    if (v !== "zh") setVoiceDialect(null);
  };
  // 1027 一键翻译:语言选项不翻译稿子(中文稿配韩语 = 出片普通话),选了外语、稿子还是中文时提示并给翻译。
  //   translatedFrom 留住译前原稿,用户可一键还原;unverifiedNumbers 是 ai 数字守卫没对上的数,标黄让人核。
  const [translating, setTranslating] = useState(false);
  const [translatedFrom, setTranslatedFrom] = useState<string | null>(null);
  const [translateUnverified, setTranslateUnverified] = useState<string[]>([]);
  // 组合试听(B 批端点;失败只 toast 不阻断出片)。
  const [previewingVoice, setPreviewingVoice] = useState(false);
  const [voicePreviewUrl, setVoicePreviewUrl] = useState<string | null>(null);
  // B5 语速滑杆(cosyvoice speech_rate 合法域 0.5~2.0;仅语速——音量/音调引擎未接,不做假滑杆)。
  const [speechRate, setSpeechRate] = useState(remake?.speechRate ?? 1.0);
  // 「走错门」换线带过来的文案(四选一里排最后:前三条都是「整稿交接」,这条只有一句文案)。
  const lineSwitch =
    recipe || remake || parseDhExplosiveState(location.state)
      ? null
      : parseDhLineSwitchState(location.state);
  const [script, setScript] = useState(
    remake?.script ?? recipe?.script ?? lineSwitch?.script ?? ""
  );
  // file 留住是给「加入我的形象」用的:enroll 是 multipart 口,只有 key 存不进形象库(0901)。
  const [avatar, setAvatar] = useState<{ key: string; url: string; file?: File } | null>(null);
  const [consent, setConsent] = useState(false);
  // 选了视频但还没勾授权 → 记住文件,勾上即自动续传(不报错、不让用户重选)。
  // 后端 AvatarProfileServiceImpl 硬门:无 consent 拒绝入库(DhConsentService 存证),
  // 所以「先传后勾」在视频路径做不到 —— 能做的是别把用户已选的文件丢掉。
  const [pendingVideo, setPendingVideo] = useState<File | null>(null);
  const [useOriginalVoice, setUseOriginalVoice] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** 形象上传的报错:上传在弹窗里做,页面级 error 被遮罩盖住 = 用户只见「点了没反应」(0928)。 */
  const [avatarError, setAvatarError] = useState<string | null>(null);

  // ── 背景音乐(2026-09-07,与视频线共库)——只在出视频的档位有意义;tts 纯音频档不挂。
  //    照视频线 VideoBgmPanel 同一套二选一语义:选平台曲 / 传自定义 mp3,互斥,默认都不选。
  const [bgmId, setBgmId] = useState<number | null>(null);
  const [customBgmObjectKey, setCustomBgmObjectKey] = useState<string | null>(null);
  const [bgmTab, setBgmTab] = useState<DhBgmTab>("preset");
  const [customBgmFile, setCustomBgmFile] = useState<File | null>(null);
  const [customBgmUrl, setCustomBgmUrl] = useState<string | null>(null);
  const [uploadingBgm, setUploadingBgm] = useState(false);
  // BGM 音量(2026-09-08 老板反馈「配乐声太小」+ 加调音量按钮):基线电平,默认 0.45(ai 侧同默认)。
  // 只在选了 BGM 时随提交下发;与 bgmId 同一「会话内设置,不进草稿回环」的口径(防 scope creep)。
  const [bgmVolume, setBgmVolume] = useState(0.45);
  // 字幕开关(2026-09-08 老板「让用户选择是否把字幕烧上去」)。默认开=0830 默认字幕的存量行为;
  // 关=提交带 burnSubtitles:false(开不发键,默认权留服务端)。纯音频档无画面,恒不渲染该控件。
  const [burnSubtitles, setBurnSubtitles] = useState(remake?.burnSubtitles ?? true);

  // ── P-C 爆款蓝图预载(location.state.explosive,一次性 ref 消费+验型;与 recipe 并存互不影响)──
  const [blueprint, setBlueprint] = useState<DhExplosiveHandoff | null>(null);
  const [scriptLines, setScriptLines] = useState<DhScriptLine[]>([]);
  const [scriptGenerating, setScriptGenerating] = useState(false);
  // 生成稿原文:清除蓝图时,脚本若未被用户改过则一并清;改过则保留(别丢用户输入)。
  const scriptFromBlueprintRef = useRef<string | null>(null);
  /**
   * E3 逐句 b-roll 配图。
   *
   * ★2026-08-08 I2:key 从**句下标**改成**句子文本**。
   *   下标是给蓝图那种「一次生成、整段不动」的稿子用的;解放到手写文案之后,用户每敲一个
   *   句号就多一句、删一句就整体前移 —— 按下标存,配好的图会挂到别的句子上去。
   *   同名重复句极少,且重复句配同一张图本来也说得通,拿文本当 key 是这里代价最小的稳妥解。
   */
  const [brollMaterials, setBrollMaterials] = useState<Record<string, BrollMaterial>>({});
  /** 用户手动标「这句切产品画面」(蓝图给的建议作初值)。 */
  const [brollOn, setBrollOn] = useState<Record<string, boolean>>({});
  const [pickingLine, setPickingLine] = useState<string | null>(null);
  const explosivePrefilledRef = useRef(false);
  /** 2026-08-14「参考片在拍什么」:纯展示态(2026-10-10 起只读折叠 —— 这块从来不上送)。 */
  const [dhReferenceContent, setDhReferenceContent] = useState("");

  // ── 2026-10-10 追爆原话复用 §3:逐句对照编辑区 ───────────────────────────────
  // linesStatus:loading=C1 进行中;ready=有逐句(进编辑区);none=无口播;failed=C1 失败。
  // none/failed 都退回「按公式生成脚本」(blueprint-script),页面不白屏。
  const [linesStatus, setLinesStatus] = useState<"idle" | "loading" | "ready" | "none" | "failed">(
    "idle"
  );
  const [lineRows, setLineRows] = useState<DhLineRow[]>([]);
  // 默认 A「原样翻拍」(1010 用户拍板);逐句单独改过档位 → null(混合态,不高亮任何预设)
  const [linePreset, setLinePreset] = useState<DhLinePreset | null>("verbatim");
  const [productFacts, setProductFacts] = useState("");
  const [productLabel, setProductLabel] = useState<string | null>(null);
  const [wantProductLine, setWantProductLine] = useState(false);
  const [rewriteHints, setRewriteHints] = useState<string[]>([]);
  // 用户直接改了「最终口播」大框 → 逐句区不再回写覆盖(显示「已手改」)
  const [scriptManualEdited, setScriptManualEdited] = useState(false);
  // 黄标确认框:要放行的动作(单条出片 / 打开批量)
  const [refFactsConfirm, setRefFactsConfirm] = useState<null | "single" | "batch">(null);
  const linesMode = !!blueprint && linesStatus === "ready";

  // 逐句区变化 → 实时拼进大框(大框仍是唯一上送源;手改过就不回写)
  useEffect(() => {
    if (!linesMode || scriptManualEdited) return;
    const composed = composeScript(lineRows);
    setScript(composed);
    scriptFromBlueprintRef.current = composed;
  }, [lineRows, linesMode, scriptManualEdited]);

  const loadBlueprintLines = useCallback(async (bp: DhExplosiveHandoff) => {
    setLinesStatus("loading");
    try {
      const r = await blueprintLinesService.analyze(bp.blueprintId);
      if (r.lines.length === 0) {
        setLinesStatus("none");
        return;
      }
      setLineRows(rowsFromLines(r.lines));
      setLinePreset("verbatim");
      setScriptManualEdited(false);
      setLinesStatus("ready");
    } catch {
      // C1 失败 → 退回按公式生成(不 toast 打断;状态卡上说明 + 可重试)
      setLinesStatus("failed");
    }
  }, []);

  /** C2 逐句改写:keep 句服务端原样返回;失败报错让重试(不出拼画面描述的兜底稿)。 */
  const runRewrite = async (rows: DhLineRow[], facts: string, addLine: boolean) => {
    if (!blueprint) return false;
    setScriptGenerating(true);
    setError(null);
    try {
      const r = await digitalHumanService.blueprintLinesRewrite({
        blueprintId: blueprint.blueprintId,
        productFacts: facts,
        lines: rewriteRequestLines(rows),
        addProductLine: addLine,
      });
      setLineRows(mergeRewrite(rows, Array.isArray(r.lines) ? r.lines : []));
      setRewriteHints(Array.isArray(r.hints) ? r.hints : []);
      setBrollMaterials({});
      setBrollOn({});
      return true;
    } catch (e) {
      setLineRows(rows);
      setError(errorText(e));
      toast(errorToast(e, { fallbackTitle: "改写没成功,可以再点一次重试" }));
      return false;
    } finally {
      setScriptGenerating(false);
    }
  };

  // 2026-08-10 返工(用户点名「适配我的产品给个产品树选择」):userInput=产品事实必传 ——
  // ai 侧 prompt 明令「输出内容只允许来自用户产品事实」,此前恒传空串等于让它空手编产品。
  // 返回是否成功,弹层按它决定收不收。2026-10-10 起只服务无口播/逐句分析拿不到的蓝图。
  const generateScript = useCallback(async (bp: DhExplosiveHandoff, productFacts: string) => {
    setScriptGenerating(true);
    setError(null);
    try {
      const r = await digitalHumanService.blueprintScript(bp.blueprintId, productFacts);
      setScript(r.script);
      setScriptLines(Array.isArray(r.lines) ? r.lines : []);
      setBrollMaterials({});
      setBrollOn({});

      scriptFromBlueprintRef.current = r.script;
      toast({ title: "口播脚本已生成", description: "已按你的产品重写,逐句可改;满意再出片。" });
      return true;
    } catch (e) {
      setError(errorText(e));
      return false;
    } finally {
      setScriptGenerating(false);
    }
  }, []);
  // 「选我的产品 / 按公式生成脚本」弹层(选产品树 → 产品事实 → 生成)
  const [adaptOpen, setAdaptOpen] = useState(false);

  useEffect(() => {
    if (explosivePrefilledRef.current) return;
    const handoff = parseDhExplosiveState(location.state);
    if (!handoff) return;
    explosivePrefilledRef.current = true;
    setDhReferenceContent(buildReferenceContent(handoff.breakdown));
    setBlueprint(handoff);
    // 2026-10-10:有口播 → C1 逐句分析进逐句对照区(默认 A 原样翻拍,零生成);
    // 不再把原话整段塞进文案框作为唯一形态。无口播(纯画面梗)→ 等用户点「按公式生成脚本」。
    const raw = transcriptTextOf(handoff.payload);
    if (raw) void loadBlueprintLines(handoff);
    else setLinesStatus("none");
  }, [location.state, loadBlueprintLines]);

  // ── 逐句区回调 ──
  const onLinePreset = (p: DhLinePreset) => {
    setLinePreset(p);
    setLineRows((rows) => applyPreset(rows, p, !!productFacts));
  };
  const onLineRowMode = (idx: number, mode: DhLineMode) => {
    setLinePreset(null);
    setLineRows((rows) => setRowMode(rows, idx, mode));
  };
  const onLineConfirm = (idx: number, confirmed: boolean) =>
    setLineRows((rows) => rows.map((r) => (r.idx === idx ? { ...r, confirmed } : r)));
  const onLinesGenerate = () => {
    if (!productFacts) {
      setAdaptOpen(true);
      return;
    }
    void runRewrite(lineRows, productFacts, wantProductLine);
  };
  const onAddProductLine = () => {
    setWantProductLine(true);
    if (!productFacts) {
      setAdaptOpen(true);
      return;
    }
    void runRewrite(lineRows, productFacts, true);
  };
  const onRemoveAddedLine = (idx: number) => {
    setWantProductLine(false);
    setLineRows((rows) => rows.filter((r) => !(r.added && r.idx === idx)));
  };
  /** 弹层「用这个产品」:换产品事实 → 品牌/产品名句自动切套句式 → 有要生成的就调 C2。 */
  const onLinesProductPicked = async (facts: string, name: string | null) => {
    const label =
      name ?? (facts.match(/^产品[::]\s*(.+)$/m)?.[1] ?? facts.split("\n")[0]).trim().slice(0, 30);
    setProductFacts(facts);
    setProductLabel(label || null);
    const next = autoSwapForProduct(lineRows);
    // 换了产品:已生成的改写稿是按旧产品写的 → 作废重来
    const reset =
      productFacts && productFacts !== facts ? next.map((r) => ({ ...r, generated: {} })) : next;
    const cleaned = reset.filter((r) => !r.added);
    setLineRows(cleaned);
    if (cleaned.some(isPending) || wantProductLine) {
      return runRewrite(cleaned, facts, wantProductLine);
    }
    return true;
  };

  // B5 场景卡 preset(dh_talking/dh_from_video):唯一验型器一次性消费,消费后清 history
  // state(刷新不重放);explosive/recipe 在场时不叠加。dh_talking = 落地默认态;
  // dh_from_video = 视频线「送去数字人」带口播脚本预填(用户已有脚本不覆盖)。
  const scenePresetRef = useRef(false);
  // B6 场景打标:preset 消费时记下,提交随请求上送(直接进页/样片墙=null 不打标)。
  const sceneKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (scenePresetRef.current || explosivePrefilledRef.current || recipe) return;
    const preset = parseScenePreset(location.state);
    if (
      !preset ||
      (preset.kind !== "dh_talking" &&
        preset.kind !== "dh_from_video" &&
        preset.kind !== "dh_tts" &&
        preset.kind !== "dh_photo")
    ) {
      return;
    }
    scenePresetRef.current = true;
    clearSceneCardState();
    sceneKeyRef.current = sceneKeyOf(preset); // B6 打标(随提交上送)
    // ★片型不再从 preset 里读(它由路由决定了),这里只消费**预填**部分。
    if (preset.kind === "dh_photo") {
      // E1 照片数字人卡:落 **上传照片**口 + 生动版(1019 照片省流版永久下架,照片档只剩这一档;
      // 卡面文案/报价同读生动版,见 scene-card catalog)。
      setMode("flagship");
      if (preset.presetAvatarId) {
        // 公共形象点选直达(hub / 形象库)→ 预选该平台形象,落「预置形象」口。
        setAvatarKind("preset");
        setPresetAvatarId(preset.presetAvatarId);
      } else {
        setAvatarKind("upload");
      }
    } else if (preset.kind === "dh_from_video") {
      setScript((s) => (s.trim() ? s : preset.script));
    } else if (preset.kind === "dh_talking" && preset.presetAvatarId) {
      // codex 修:公共形象点选直达 → 预选该平台形象
      setAvatarKind("preset");
      setPresetAvatarId(preset.presetAvatarId);
    }
    // B5 二刀:声音库「用 TA 出片」直达 → 预选该内置音色。
    // ★三条线共用这一段(2026-08-08):声音库分流后,同一个「用 TA 出片」可能落任意一条线,
    //   预填音色的动作三条线**逐字相同**,留在 dh_talking 分支里等于另两条线静默丢音色。
    //   真人口播线默认「用原声」会把音色控件整个藏掉 → 同时切「换一个音色」,让预选可见可改
    //   (另两条线没有 useOriginalVoice 这个概念,置 false 无副作用)。
    if (preset.kind !== "dh_from_video" && preset.voiceProfileId != null) {
      // 声音 V2(2026-09-08):声音库「我的声纹」卡直达 → 选中该声纹(口径同 presetVoice 分支:
      // 真人口播线默认「用原声」会藏掉音色控件,同步切换让预选可见可改)。
      setVoiceKind("profile");
      setProfileId(String(preset.voiceProfileId));
      setUseOriginalVoice(false);
    } else if (preset.kind !== "dh_from_video" && preset.presetVoice) {
      setVoiceKind("preset");
      setVoice(preset.presetVoice);
      setUseOriginalVoice(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const clearBlueprint = () => {
    setBlueprint(null);
    setScriptLines([]);
    setLinesStatus("idle");
    setLineRows([]);
    setLinePreset("verbatim");
    setRewriteHints([]);
    setWantProductLine(false);
    setScriptManualEdited(false);
    setBrollMaterials({});
    setBrollOn({});
    if (scriptFromBlueprintRef.current !== null && script === scriptFromBlueprintRef.current) {
      setScript("");
    }
    scriptFromBlueprintRef.current = null;
  };

  // 形象库按片型过滤:选不到就走不错管线(旧设计混列,选中后才派生模式 = 用户能选出矛盾组合)。
  // ★扁平形象列表退役后这里恒空,保留只为不改动下游 map 的形状(见 avatarProfiles 注释)。
  const visibleProfiles: never[] = avatarProfiles;

  // 切片型 → 清掉上一档选的形象/待传文件(跨档残留会让「出片」按错误管线提交)。
  /**
   * 「换一种做法」= **换一页**,不是页面里换个片型。
   * ★带上已写好的文案过去(用户改主意的是"用什么做",不是"讲什么"),形象/声音不带 ——
   *   那些是每条线各自的原料,跨线复用只会带出错配(A1/A2 那两个缺陷的根)。
   */
  const onSwitchLine = (next: DhLine) => {
    if (next === line) return;
    navigate(DH_LINE_ROUTE[next], {
      state: script.trim() ? { dhCarryScript: script } : undefined,
    });
  };

  // R1-b:形象选择器改右侧抽屉(拍板)。默认关 —— 页面上留 chip 回显选了谁。
  const [avatarSheetOpen, setAvatarSheetOpen] = useState(false);
  /** I4 配音线:按用途筛音色(usage 字段一直在,此前只有声音库页用过)。 */
  const [voiceUsage, setVoiceUsage] = useState<string | null>(null);

  // ── R5 创建页草稿(纯前端 localStorage;不存上传照片的临时 key,见 dhDraft 头注)──
  const draftIdentity = useRef(readDraftIdentity()).current;
  const draftKey = dhDraftKey(draftIdentity.tenantCode, draftIdentity.userId, line);
  /** 进页那一刻读到的草稿。null = 没有;**不自动铺**,只在条上给两个明确出口。 */
  const [draft, setDraft] = useState<DhDraft | null>(null);
  const draftLoadedRef = useRef(false);

  // ── R4 批量出片:一份文案 × N 个形象 ─────────────────────────────────────
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchItems, setBatchItems] = useState<DhBatchItem[] | null>(null);
  const [batchResult, setBatchResult] = useState<DhBatchResult | null>(null);
  const [batchRunning, setBatchRunning] = useState(false);
  const batchAbortRef = useRef(false);

  // 1020 视频档时长上限按出片通道从服务端取(新通道 5 分钟 / 老通道 2 分钟,见 model/dhVideoLimits)。
  const videoLimits = useDhVideoLimits();
  const videoScriptMax = videoLimits.maxScriptChars;
  const videoMaxLabel = dhDurationLabel(videoLimits.maxScriptSec);

  // 三条线的能力文案(视频/照片来自共享常量,纯音频档是 console 独有的一档)。
  const filmTypeChoices = [
    ...filmTypeCards({ resolutionChoice: true, videoMaxLabel }),
    {
      value: "tts" as const,
      label: "文生语音(只要声音)",
      recommended: false,
      summary: "不出画面,只合成一段口播音频;配音 / 电话 / 播报用。",
      bullets: ["无需形象,选音色即可", "支持克隆音色与语速", "按音频秒数计费,便宜一档"],
    },
  ];

  const currentLineCard = filmTypeChoices.find((c) => c.value === filmType) ?? filmTypeChoices[0];
  // 另外两条线:选错门时横跳,文案带过去。
  const switchOptions = (["talking", "photo", "tts"] as DhLine[])
    .filter((l) => l !== line)
    .map((l) => ({
      key: l,
      title: DH_LINE_META[l].title,
      hint: DH_LINE_META[l].switchHint,
      onPick: () => onSwitchLine(l),
    }));

  // I5 出片前把钱说清:按当前档位 × 当前字数实时估。
  // ★ 单价与卡面同一个出处 —— 2026-08-26 起是**后端价目表**,不是前端常量。
  //   此前的前端常量停在 ×10 时代(视频档 80 / 旗舰 1000 每秒),而实扣早已按价目表走
  //   (现价:生动版固定 168/秒,其余档按执行口径),那份常量对着用户高报好几倍 —— 所以这里
  //   只认后端价目表,前端不再存一份单价。
  const { book: priceBook } = usePriceBook();
  // Z2 追加(0926):规格档查 api 给的表(以 ai 执行侧计费 model 为准),不在前端映射。
  const dhVariants = useDhPriceVariants();
  const cost = estimateDhCost(
    priceBook,
    isTts ? "tts" : isVideoAvatar ? "video" : mode,
    script.trim().length,
    resolution,
    dhVariants
  );
  const dhQuoteVariant = dhVariantOf(
    isTts ? "tts" : isVideoAvatar ? "video" : mode,
    resolution,
    dhVariants
  );
  /**
   * ★ 屏幕上那个「预计 ≈ N 积分」**改成后端报价口现算的数**(`dryRun: true`)。
   *
   * 上一版它是 `单位单价 × 秒数` —— 单价是从价目表取的(对的),但**乘法在前端**,
   * 而后端的公式是 `⌈成本 × qty_scale × 实际量 × markup ÷ 面值⌉`:取整发生在**总额**上。
   * 「单位取整再乘」在 DERIVED 的线上会逐单位多出一点,秒数越长差得越多 ——
   * 而提交时绑的那张单走的是后端公式。⇒ 看到的和扣的**又不是同一个数**,只是差得小了。
   *
   * 现在两边同源:这里问 dryRun、提交时问正式单,**同一个 sku+规格+量**。
   * 取不到价 → null → 屏幕上不出数字(不退回本地乘法,那等于又造一个出处)。
   */
  const dhQuoteParams =
    cost.seconds > 0 && dhQuoteVariant != null
      ? { skuCode: SKU.dhRender, variantKey: dhQuoteVariant, qty: cost.seconds }
      : null;
  const { credits: dhQuotedCredits } = useQuotePreview(dhQuoteParams);
  // B2 #16 → 0927 K4 I:算式里的每秒单价读目录不取整列(creditsPerUnitExact,与 hub 卡面同一列),
  // 不再用目录整数上界(省流版 4 → 3.5),也不再另发一次 dryRun 报价去探。
  const dhPerSecondRate = useDhPerSecondRate(dhQuoteVariant);

  // 文案上限:三个口径(视频档按出片通道 540 或 1350 / 纯音频 2000 / 照片生动版 90)。
  // ★照片档从前一律 800 —— 但生动版上游硬限 20 秒 ≈ 90 字,标 800 实收 90 是 0813 反馈里
  //   撞得最多的一条(见 FLAGSHIP_SCRIPT_MAX 注释)。上限跟着档位走,超了在输入处就说。
  const scriptMax = isVideoAvatar ? videoScriptMax : isTts ? TTS_SCRIPT_MAX : FLAGSHIP_SCRIPT_MAX;
  // 生动版超限单独判:maxLength 拦得住手打,拦不住 AI 生成脚本直接 setState 写进来的长文案。
  const flagshipTooLong =
    !isVideoAvatar && !isTts && mode === "flagship" && script.trim().length > FLAGSHIP_SCRIPT_MAX;

  // packs W4 分身选人:按人物库真人人物一键带出其挂钩的形象(+声纹)。真人形象与声纹授权入库后
  // 全公司共用(跨公司隔离、删除仅本人或管理员);人物没有可用素材 → 明说,不静默失败。
  const pickCharacter = (pick: CharacterPick) => {
    const c = pick.character;
    // ★0915 归一:人物的脸就是它 identity 槽里的素材 —— 不再经 avatarProfileId 那条指针,
    //   也不再有 owner-only 那道「这位人物的形象不是你的」拦截(批示一:公司共用)。
    const want = filmType === "video" ? "video" : "image";
    // ★1006:分身素材从 dhMaterials 挑(dh_source 槽优先,再接 identity),不再只看 identity。
    const m = pick.dhMaterials.find((x) => (x.kind ?? "image") === want) ?? pick.dhMaterials[0];
    if (!m) {
      toast({
        title: "这位人物还没有素材",
        description: "去人物库打开 TA,在「分身口播素材」区传一张照片或一段视频。",
        variant: "destructive",
      });
      return;
    }
    // ★素材介质与本页不匹配 → **明说去哪一页**,不偷偷改片型
    //   (旧行为自动切档正是 A1/A2 两个缺陷的根:片型悄悄变了、回显与参数还停在原档)。
    const mKind = (m.kind ?? "image") as "image" | "video";
    const wantLine: DhLine = mKind === "video" ? "talking" : "photo";
    if (wantLine !== line) {
      toastGoToLine(
        wantLine,
        `「${c.name}」这条素材是${mKind === "video" ? "视频" : "照片"}`,
        "这种素材在那条线出片"
      );
      return;
    }
    setAvatarKind("profile");
    setAvatarProfileId(String(m.id));
    setPickedMaterial({
      label: m.label || c.name,
      thumbUrl: m.thumbUrl ?? m.url ?? null,
      kind: mKind,
    });
    if (c.voiceProfileId) {
      setVoiceKind("profile");
      setProfileId(String(c.voiceProfileId));
    } else if (c.presetVoice) {
      setVoiceKind("preset");
      setVoice(c.presetVoice);
    }
    toast({
      title: `已按「${c.name}」带出素材${c.voiceProfileId || c.presetVoice ? "与声音" : ""}`,
      description: "填好文案就能出片;声音仍可手动换。",
    });
  };

  /**
   * 把刚上传的这张脸**存成一个人物**(0915 归一批 B)。
   *
   * ★归一前这里叫「存成形象」——形象表并入素材表之后,素材必须有主人,
   * "存一张无主的脸"这个中间态不存在了。用户要的价值没变(下次能复用),
   * 变的是它落成什么:一个人物 + 它 identity 槽里的一条素材。
   *
   * 人物名先用文件名占位(与视频线/H5 同一命名口径),用户可以去人物库改。
   */
  const createCharacterWithFreeName = async (base: string) => {
    for (let i = 1; ; i++) {
      const name = i === 1 ? base : `${base}_${i}`;
      try {
        return await characterService.create({ name, type: "real" });
      } catch (e) {
        const clash = e instanceof ApiError && e.message.includes("同名人物已存在");
        if (!clash || i >= 20) throw e;
      }
    }
  };

  const saveAsCharacter = useCallback(
    async (file: File) => {
      const base = file.name.replace(/\.[^.]+$/, "").slice(0, 20) || "新人物";
      // ★0928 爱民:微信视频文件名截 20 字只精确到十秒,重传同一段/同十秒另一段就撞
      //   「同名人物已存在」(400),此前整条上传卡死。撞名自动加后缀,人物名可去人物库改。
      // ★type 必须显式 real:缺省按虚拟人建(后端 PackCharacterUpsertRequest 头注),
      //   这条路用户勾了肖像本人授权、传的就是真人 —— 0928 测试机 5 个真人被标成虚拟人。
      const c = await createCharacterWithFreeName(base);
      // ★1006:分身仓上传落 dh_source 槽(分身口播素材),不再落 identity ——
      //   identity 是真人出镜身份素材,分身仓建的人物混进真人出镜选人就是这条路造成的。
      const m = await characterService.uploadMaterial(c.id, file, DH_SOURCE_SLOT);
      await characterService.setPrimaryMaterial(c.id, m.id);
      await qc.invalidateQueries({ queryKey: ["characters"] });
      return { character: c, material: m };
    },
    [qc]
  );

  // 真人视频(主推):存成人物(consent 随存留证)→ 自动选中 → 走视频换口播。
  // 只在已授权时调用(后端硬门);调用方负责先验 consent。
  const enrollVideo = useCallback(
    async (file: File) => {
      setUploading(true);
      setAvatarError(null);
      try {
        const { character, material } = await saveAsCharacter(file);
        setAvatarKind("profile");
        setAvatarProfileId(String(material.id));
        setPickedMaterial({
          label: character.name,
          thumbUrl: material.thumbUrl ?? material.url ?? null,
          kind: "video",
        });
        // R1-b:视频入库是**一步到位**的(consent 早已勾过、形象已选中)→ 收抽屉,
        // 让用户直接回到文案。照片路径不在这里收:那条路还要在抽屉里勾肖像授权。
        setAvatarSheetOpen(false);
        toast({
          title: "已存成人物并选中",
          description: "填好文案就能用原声出片。",
        });
      } catch (e) {
        setAvatarError(errorText(e));
      } finally {
        setUploading(false);
      }
    },
    [qc, toast]
  );

  /**
   * 0901「加入我的形象」:PC 照片线此前是四格里唯一不入形象库的路(PC 视频线自动入库、
   * H5 两档都入库),且批量候选只吃形象库 —— 照片上传的人永远进不了批量。
   * 显式按钮而非照抄视频线自动入库:库里已见自动入库产生的重复,且每人配额 20 —— 存不存让用户说。
   * 落点与视频线一致:存完切「我的形象」选中(upload 态自然离场,顺手防了重复点击)。
   */
  const [savingToProfiles, setSavingToProfiles] = useState(false);
  const saveUploadedToProfiles = async () => {
    if (!avatar?.file || savingToProfiles) return;
    setSavingToProfiles(true);
    try {
      const { character, material } = await saveAsCharacter(avatar.file);
      setAvatarKind("profile");
      setAvatarProfileId(String(material.id));
      setPickedMaterial({
        label: character.name,
        thumbUrl: material.thumbUrl ?? material.url ?? null,
        kind: "image",
      });
      setAvatar(null);
      toast({
        title: `已存成人物「${character.name}」`,
        description: "下次在「按人物选」里直接选 TA;人物名可以去人物库改。",
      });
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: "保存失败" }));
    } finally {
      setSavingToProfiles(false);
    }
  };

  // 勾上授权 → 把之前选好的视频自动续传(用户不用重选文件)。
  // 先清 pendingVideo 再传:避免 enroll 期间 effect 因别的 dep 变化重跑而二次上传。
  useEffect(() => {
    if (!consent || !pendingVideo) return;
    const file = pendingVideo;
    setPendingVideo(null);
    void enrollVideo(file);
  }, [consent, pendingVideo, enrollVideo]);

  /**
   * 「走错门」提示 —— **必须带一个真能点的门**。
   *
   * 2026-08-12 测试反馈(月月):在真人口播页传照片,反复弹「到那边传,文案会带过去」却传不上去。
   * 拦截本身是对的(每页只收自己那种原料,见下方注释),坏在这个提示是**死胡同**:toast 只有标题
   * 和说明、没有任何可点的东西,而另一处同族文案还写着「点这里过去」—— 界面上并不存在那个「这里」。
   * 用户只能反复重试。现在给一个真按钮,并把已填文案带过去(说到做到)。
   */
  const toastGoToLine = (target: DhLine, title: string, why: string) => {
    const meta = DH_LINE_META[target];
    toast({
      title,
      description: `${why}。点右边过去,${script.trim() ? "已填的文案会带过去" : "文案会带过去"}。`,
      variant: "destructive",
      action: (
        <ToastAction
          altText={`去${meta.title}`}
          onClick={() =>
            navigate(DH_LINE_ROUTE[target], {
              state: script.trim() ? { dhLineSwitch: { script } } : undefined,
            })
          }
        >
          去{meta.title}
        </ToastAction>
      ),
    });
  };

  const onPickAvatar = async (file: File | undefined) => {
    if (!file) return;
    setAvatarError(null);
    const isVideo = VIDEO_TYPES.test(file.type);
    if (!isVideo && !PHOTO_TYPES.test(file.type)) {
      setAvatarError("形象仅支持照片(png / jpeg / webp)或视频(mp4 / mov)");
      return;
    }
    // ★走错门:每页只收自己那种原料。旧行为是**自动改片型**(A1/A2 两个缺陷的根 ——
    //   片型悄悄变了、回显与参数还停在原档),独立页之后改成明说去哪一页。
    if (isVideo && line !== "talking") {
      setAvatarError(null);
      toastGoToLine("talking", "这是一段视频", "视频形象在这里出片,保留原片画面换台词");
      return;
    }
    if (!isVideo && line === "talking") {
      setAvatarError(null);
      toastGoToLine("photo", "这是一张照片", "一张正脸照就能开口");
      return;
    }
    if (isVideo) {
      if (file.size > MAX_VIDEO_MB * 1024 * 1024) {
        setAvatarError(avatarVideoSizeError(file) ?? "视频过大");
        return;
      }
      // 0908 老板「分辨率不够别让传」:时长/分辨率前置闸(读 metadata,读不到放行——
      // 量具故障不算用户的错)。此前超界的片要等出片阶段才报 InvalidFile.Resolution,白等十分钟。
      const meta = await readVideoMeta(file);
      const metaErr =
        avatarVideoDurationError(meta.seconds, videoLimits) ??
        avatarVideoResolutionError(meta.width, meta.height);
      if (metaErr) {
        setAvatarError(metaErr);
        return;
      }
      if (!consent) {
        // 不是死胡同:记住文件 + 提示差一步,勾上授权后由上面的 effect 自动续传。
        setPendingVideo(file);
        setAvatarError(null);
        return;
      }
      await enrollVideo(file);
      return;
    }
    setPendingVideo(null); // 改选照片 → 丢弃待传视频,避免勾授权时误传旧文件
    // 照片:一次性临时上传(默认不入形象库);想复用/进批量走「加入我的形象」按钮(0901)。
    // consent 在出片按钮门控。
    setUploading(true);
    setAvatarError(null);
    try {
      const { key, url } = await digitalHumanService.uploadAvatar(file);
      setAvatar({ key, url, file });
    } catch (e) {
      setAvatarError(errorText(e));
    } finally {
      setUploading(false);
    }
  };

  /**
   * I2「b-roll 解放」:逐句条不再是爆款蓝图的专属。
   *
   * ★这是分身舱唯一「比别家多一手」的能力(真插播帧证在 D1 走查里验过),
   *   却一直锁在爆款入口后面 —— 手写文案的用户根本见不到。
   * 蓝图态:用生成稿的逐句(带 broll 建议);手写态:按句末标点实时拆(与详情页台本同一个拆法)。
   */
  // 2026-10-10:逐句对照态用逐句区的句子(手改过大框则回到按标点拆,以大框为准)
  const effectiveLines: DhScriptLine[] =
    linesMode && !scriptManualEdited
      ? linesForBroll(lineRows)
      : blueprint && !linesMode && scriptLines.length > 0
        ? scriptLines
        : splitScriptLines(script).map((text) => ({ text, broll: false }));
  const isBrollOn = (ln: DhScriptLine) => brollOn[ln.text] ?? ln.broll;
  // 真正会产生插播的句数(标了 b-roll 且配了图);0 = 出片与改造前完全一致
  const brollCount = effectiveLines.filter((ln) => isBrollOn(ln) && brollMaterials[ln.text]).length;

  const voiceReady = (isVideoAvatar && useOriginalVoice) || voiceKind === "preset" || !!profileId;
  const avatarReady = isTts
    ? true // 纯音频档无形象
    : avatarKind === "preset"
      ? !!presetAvatarId
      : avatarKind === "profile"
        ? allowPhoto && !!avatarProfileId
        : allowPhoto && !!avatar && consent;
  const scriptReady =
    script.trim().length > 0 &&
    (!isVideoAvatar || script.trim().length <= videoScriptMax) &&
    (!isTts || script.trim().length <= TTS_SCRIPT_MAX) &&
    !flagshipTooLong;
  // scriptGenerating 也冻结出片(codex 修:重新生成中提交会用旧脚本+当前 blueprintId,档案错配)。
  const canSubmit =
    avatarReady &&
    scriptReady &&
    voiceReady &&
    !uploading &&
    !submitting &&
    !scriptGenerating &&
    !uploadingBgm;

  // 视频档「用原声」:不点名任何音色,服务端从源视频抽音轨克隆原声念新文案。
  // ★声音 V2 三件套只在 profile 路带(单条/批量/tts 三个提交点全走这一份构造,
  //   字段跟着这里走就不会「单条带了批量丢」—— 这个调用点踩过 sceneKey/quoteId 两次同型坑)。
  //   预置音色是旧引擎,带上也被忽略,不塞噪声字段。
  // 三件套(语言/方言/演绎)解锁面 = 克隆声线:我的声纹,或视频档「用原声」(服务端抽音轨
  // 克隆 qwen 临时声纹,ai 的 synthesize_speech 同样吃这三个字段)。预置音色是旧引擎 → 置灰。
  // ★1006:旧版声纹(supportsTuning===false,cosyvoice 时代录的)三件套不生效 → 同样锁。
  //   字段缺省(老后端)按支持处理,不误锁(见 voiceProfileSupportsTuning)。
  const selectedVoiceProfile =
    voiceKind === "profile" ? voiceProfiles.find((p) => String(p.id) === profileId) : undefined;
  const legacyProfileSelected =
    !(isVideoAvatar && useOriginalVoice) &&
    !!selectedVoiceProfile &&
    !voiceProfileSupportsTuning(selectedVoiceProfile);
  const voiceTuningUnlocked =
    (isVideoAvatar && useOriginalVoice) || (voiceKind === "profile" && !legacyProfileSelected);

  const runTranslate = async () => {
    const source = script;
    setTranslating(true);
    try {
      const r = await digitalHumanService.scriptTranslate(source, voiceLanguage);
      setScript(r.text);
      if (linesMode) setScriptManualEdited(true);
      setTranslatedFrom(source);
      setTranslateUnverified(Array.isArray(r.unverifiedNumbers) ? r.unverifiedNumbers : []);
      toast({
        title: `已翻译成${languageZhName(voiceLanguage)}`,
        description: "请过一遍译文再出片;不满意可以直接改,或还原中文稿。",
      });
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: "翻译没成功,可以再点一次重试" }));
    } finally {
      setTranslating(false);
    }
  };
  const restoreTranslated = () => {
    if (translatedFrom == null) return;
    setScript(translatedFrom);
    if (linesMode) setScriptManualEdited(translatedFrom !== composeScript(lineRows));
    setTranslatedFrom(null);
    setTranslateUnverified([]);
  };
  // 只在语言真生效时提示(预置/老声纹锁着语言,选了也不生效,不该催人翻译)。
  const scriptNeedsTranslate =
    voiceTuningUnlocked && !scriptGenerating && looksUntranslated(script, voiceLanguage);

  const voiceFieldsOf = () =>
    isVideoAvatar && useOriginalVoice
      ? {
          // ★0908 老板「克隆了声音之后无法选择演绎」:「用原声」在服务端就是克隆
          //   (抽音轨→qwen 临时声纹→TTS 念新文案),ai 侧 synthesize_speech 一直在读
          //   这三个字段 —— 此前这里剪掉它们,等于把最像真人的那条路锁在 natural。
          voiceSource: "preset",
          voiceLanguage,
          voiceDialect: voiceLanguage === "zh" ? voiceDialect : null,
          voiceStyle,
        }
      : voiceKind === "profile" && legacyProfileSelected
        ? // 旧版声纹:三件套不生效,不上送(锁定态下界面上的值只是残影,不该进请求)。
          { voiceSource: "profile", voiceRef: profileId }
        : voiceKind === "profile"
          ? {
              voiceSource: "profile",
              voiceRef: profileId,
              voiceLanguage,
              // state 已保证「非中文 ⇒ null」(onChangeVoiceLanguage 顺手收方言),这里再收一次口
              voiceDialect: voiceLanguage === "zh" ? voiceDialect : null,
              voiceStyle,
            }
          : { voiceSource: "preset", presetVoice: voice };

  /**
   * 声音 V2 组合试听:按「声纹 × 语言 × 方言 × 演绎 × 语速」现合成一小段。
   * 文案取前 30 字(空文案用占位句)—— 试听是给「组合对不对」听个感觉,不是全文预渲染。
   * 只对声纹开(能力规则同上);失败 toast 不落 error 态(error 位是出片主链路的)。
   */
  const previewVoiceCombo = async () => {
    if (!profileId || previewingVoice) return;
    setPreviewingVoice(true);
    try {
      const text = script.trim().slice(0, 30) || "大家好,先听听这个声音组合念起来是什么感觉。";
      const r = await digitalHumanService.previewVoice({
        voiceProfileId: Number(profileId),
        text,
        voiceLanguage,
        voiceDialect: voiceLanguage === "zh" ? voiceDialect : null,
        voiceStyle,
        speechRate,
      });
      setVoicePreviewUrl(r.audioUrl);
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: "试听失败" }));
    } finally {
      setPreviewingVoice(false);
    }
  };

  // 预置形象:平台安全资产,免 consent;我的形象:consent 已在保存时存证;上传照片:consent 已在 avatarReady 门控。
  // tts 纯音频档无形象:不算 avatarFields(upload 分支的 avatar!.key 在 tts 下是空指针)。
  // 仍带 consentAcknowledged:请求体该字段必填,少了它整个 spread 的类型就塌成可选。
  const avatarFieldsOf = () =>
    isTts
      ? { consentAcknowledged: false }
      : avatarKind === "preset"
        ? { avatarSource: "preset", avatarRef: presetAvatarId, consentAcknowledged: false }
        : avatarKind === "profile"
          ? { avatarSource: "profile", avatarRef: avatarProfileId, consentAcknowledged: false }
          : {
              avatarSource: "upload_photo",
              avatarRef: avatar!.key,
              consentAcknowledged: consent,
            };

  /**
   * 2026-10-10 §3.2(替换 BPWB-1「一字未改」确认框):不拦原话,拦「别人家的事实变成你的事实」——
   * 最终口播里还留着的黄标句(参考片数字/故事/品牌…,或改写句里产品事实外的数字)没勾确认 → 弹框。
   * 确认后建任务带 refFactsConfirmed:true(落任务 settings,售后可追溯)。
   */
  const pendingRefFacts = linesMode ? unconfirmedFlagged(lineRows, script) : [];
  const hasFlaggedInScript =
    linesMode && lineRows.some((r) => isFlagged(r) && script.includes(rowText(r)));
  const pendingRewriteCount =
    linesMode && !scriptManualEdited ? lineRows.filter(isPending).length : 0;

  // ── 背景音乐(与视频线 VideoBgmPanel 同规矩)────────────────────────────────
  // 选平台曲(含选「不配乐」)→ 一律先清自定义,避免「传过自定义又切回平台」时旧 key 残留。
  const handleSelectPresetBgm = (id: number | null) => {
    setCustomBgmObjectKey(null);
    setBgmId(id);
  };
  const handleClearCustomBgm = () => {
    setCustomBgmFile(null);
    setCustomBgmUrl(null);
    setCustomBgmObjectKey(null);
  };
  const handleCustomBgmUpload = async (f: File) => {
    if (!/^audio\/(mpeg|mp3)$/.test(f.type)) {
      setError("仅支持 audio/mpeg (mp3)");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setError("BGM 文件需 ≤10MB");
      return;
    }
    setUploadingBgm(true);
    setError(null);
    try {
      const { objectKey, url } = await bgmService.uploadCustomBgm(f);
      setCustomBgmFile(f);
      setCustomBgmUrl(url);
      setBgmId(null);
      setCustomBgmObjectKey(objectKey);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setUploadingBgm(false);
    }
  };

  /** 出片/批量前的共用闸:待生成的改写句、未确认的黄标句。返回 true=可以直接放行。 */
  const passLinesGate = (action: "single" | "batch") => {
    if (pendingRewriteCount > 0) {
      toast({
        title: `还有 ${pendingRewriteCount} 句没生成`,
        description:
          "你选了套句式/重写的句子还是参考片原话 —— 先点逐句区的「生成改写」,或把它们改回「原话」。",
        variant: "destructive",
      });
      return false;
    }
    if (pendingRefFacts.length > 0) {
      setRefFactsConfirm(action);
      return false;
    }
    return true;
  };

  const onSubmit = () => {
    if (!passLinesGate("single")) return;
    void submitTask(hasFlaggedInScript);
  };

  const onOpenBatch = () => {
    if (!passLinesGate("batch")) return;
    setBatchItems(null);
    setBatchResult(null);
    setBatchOpen(true);
  };

  /** 确认框「确认可用」:把列出的句子全部记为已确认,再放行原动作。 */
  const onRefFactsConfirmed = () => {
    const action = refFactsConfirm;
    const ids = new Set(pendingRefFacts.map((r) => r.idx));
    setLineRows((rows) => rows.map((r) => (ids.has(r.idx) ? { ...r, confirmed: true } : r)));
    setRefFactsConfirm(null);
    if (action === "single") void submitTask(true);
    else if (action === "batch") {
      setBatchItems(null);
      setBatchResult(null);
      setBatchOpen(true);
    }
  };

  const submitTask = async (refFactsConfirmed = false) => {
    if (scriptGenerating) return; // 双保险(disabled 之外再 guard 一次)
    setSubmitting(true);
    setError(null);
    try {
      const voiceFields = voiceFieldsOf();
      const avatarFields = avatarFieldsOf();
      // ★ 批 L:提交这一刻落一张**正式报价单**,单号随建单请求带给后端绑到 action_id。
      //   档位 → 规格档 key 走 `dhVariantOf`(与页内估算器同一处映射,不另抄一份);
      //   量 = 估算秒数(与后端 DhCreditPrecheck 的 estSeconds 同口径:4.5 字/秒)。
      //   报价失败不阻断提交(返 null)—— 理由见 acquireQuoteId 的注释。
      //   ⚠️ 已知缺口:页面上「预计 ≈ N 积分」仍来自 estimateDhCost,**不是这张单的数**。
      //      绑单解决的是「按哪个价扣」,不解决「看到的和扣的是否同一个数」。
      const dhQuoteId = await acquireQuoteId(dhQuoteParams);
      const { taskId } = await digitalHumanService.createTask(
        isTts
          ? {
              // B5.5 纯音频档:无形象字段;音色/语速/文案同口播档语义
              mode: "tts",
              ...voiceFields,
              script: script.trim(),
              speechRate,
              consentAcknowledged: false,
              ...(sceneKeyRef.current ? { sceneKey: sceneKeyRef.current } : {}), // B6 场景打标
              ...(dhQuoteId ? { quoteId: dhQuoteId } : {}), // 批 L 报价单号
              // codex 修:蓝图脚本可在本页切到纯音频档出片 —— 引用不随行会丢取用登记与套路档案。
              ...(blueprint ? { blueprintId: blueprint.blueprintId } : {}),
              // 2026-10-10 §3.2:黄标句已确认 → 落任务 settings(两个分支都带,同 quoteId 的教训)
              ...(refFactsConfirmed ? { refFactsConfirmed: true } : {}),
            }
          : {
              mode: isVideoAvatar ? "video" : mode,
              ...avatarFields,
              ...voiceFields,
              // 清晰度只在照片档是真参数;视频档跟随源视频,videoretalk 根本不吃该字段。
              // 旧代码无条件传 → 生产实证脏参数 `dh_submitted mode=video model=videoretalk resolution=480P`
              // (不影响计费:billing_model_code 只在 flagship 才看 resolution)。
              ...(isVideoAvatar ? {} : { resolution }),
              // 表演风格只在生动版是真参数(长口播/视频档引擎不吃;后端白名单闸同口径)。
              ...(!isVideoAvatar && mode === "flagship" ? { performanceStyle } : {}),
              script: script.trim(),
              // B5 语速:所有 console 出片路径都是 script TTS(含视频档「用原声念新文案」)→ 恒透传。
              speechRate,
              // 字幕开关(0908):开=不发键(服务端默认烧,默认权留那边);关才发 false。
              ...(burnSubtitles ? {} : { burnSubtitles: false }),
              // codex 修:dh_talking / dh_from_video 卡进来的任务也要打标(此前只有 tts 分支带)。
              ...(sceneKeyRef.current ? { sceneKey: sceneKeyRef.current } : {}),
              // ★ 批 L 报价单号 —— **两个分支都要带**。这个 createTask 是三元表达式的两支,
              //   只给一支加字段是这个调用点踩过的坑(上一行那条 codex 修就是同一处:
              //   sceneKey 当初也只有 tts 分支带)。
              ...(dhQuoteId ? { quoteId: dhQuoteId } : {}),
              // E3:一句都没配图就不带 brollLines —— 后端与引擎完全走原有单段合成路径,
              // 不为「可能要插播」白白把整段合成拆成逐句(逐句拼接会多 2.3% 时长=多 2.3% 钱)。
              ...(brollCount > 0
                ? {
                    // W5:素材来源二选一 —— 我的作品带 assetId,产品物料带 packMaterialId
                    //(后端 resolver 契约:两个都带=VALIDATION_ERROR,这里的展开互斥保证不会)。
                    brollLines: effectiveLines.map((ln) => {
                      const on = isBrollOn(ln);
                      const m = brollMaterials[ln.text];
                      return {
                        text: ln.text,
                        broll: on,
                        ...(on && m?.assetId ? { assetId: m.assetId } : {}),
                        ...(on && m?.packMaterialId ? { packMaterialId: m.packMaterialId } : {}),
                      };
                    }),
                  }
                : {}),
              useOriginalVoice: isVideoAvatar && useOriginalVoice,
              // P-C 蓝图引用:backend 派发成功后自动登记取用 + 服务端解析套路名落档案。
              ...(blueprint ? { blueprintId: blueprint.blueprintId } : {}),
              // 2026-10-10 §3.2:黄标句已确认 → 落任务 settings(两个分支都带,同 quoteId 的教训)
              ...(refFactsConfirmed ? { refFactsConfirmed: true } : {}),
              // 背景音乐(2026-09-07):二选一,都不选就不带任何字段 = 存量「不配乐」行为不变。
              // 只在出视频的分支带(tts 分支在上面,产物是音频,不吃 BGM)。
              ...(bgmId != null ? { bgmId } : {}),
              ...(customBgmObjectKey ? { customBgmObjectKey } : {}),
              // 音量只在真选了 BGM 时才有意义 —— 没配乐带音量是噪声字段。
              ...(bgmId != null || customBgmObjectKey ? { bgmVolume } : {}),
            }
      );
      // ★出片成功 → 清草稿:这稿已经变成任务了,留着只会在下次进页时勾起一份做完的活。
      //   注意「取消」不清 —— 取消是「我先不出了」,不是「我不要这稿了」。
      clearDhDraft(draftKey);
      toast({ title: "已提交出片", description: "口播视频生成中,详情页会实时更新进度。" });
      navigate(`/ai-digital-human/tasks/${taskId}`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSubmitting(false);
    }
  };

  // ── R5 草稿:进页读一次(不自动铺);之后随表单改动防抖存 ────────────────────
  useEffect(() => {
    if (draftLoadedRef.current) return;
    draftLoadedRef.current = true;
    // ★别人替你填好的一稿在场时不提草稿:样片 recipe / 爆款蓝图 / 「回工作台改这稿」
    //   进来的用户，意图明确就是用这一份，弹一条「要不要恢复上次」纯属打岔。
    if (recipe || remake || parseDhExplosiveState(location.state)) return;
    setDraft(loadDhDraft(draftKey));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  useEffect(() => {
    // ★恢复条还没落定(「用这份/丢掉」都没点)之前不自动存 —— 否则用户只是进来看一眼、
    //   在框里敲一个字,600ms 后上一份没做完的稿就被这一个字覆盖掉了。
    //   (长图侧同款坑已填过,见 useLongImageBuilder 的「挂载即跑」头注。)
    if (draft) return;
    const t = setTimeout(() => {
      saveDhDraft(draftKey, {
        script,
        mode,
        resolution,
        voiceKind,
        voice,
        profileId,
        speechRate,
        performanceStyle,
        useOriginalVoice,
        // 声音 V2 三件套(2026-09-08):不进草稿就会「恢复的稿把选好的粤语静默换回普通话」
        // —— 与 useOriginalVoice 当年 v1 不存的坑同型。
        voiceLanguage,
        voiceDialect,
        voiceStyle,
        burnSubtitles,
        brollOn,
        // ★上传口不存引用:临时 key 过期即死链,只记「传过」这个事实。
        avatarKind: avatarKind === "upload" ? null : avatarKind,
        avatarRef:
          avatarKind === "preset"
            ? presetAvatarId
            : avatarKind === "profile"
              ? avatarProfileId
              : "",
        hadUpload: avatarKind === "upload" && !!avatar,
      });
    }, 600);
    return () => clearTimeout(t);
  }, [
    draft,
    draftKey,
    script,
    mode,
    resolution,
    voiceKind,
    voice,
    profileId,
    speechRate,
    performanceStyle,
    useOriginalVoice,
    voiceLanguage,
    voiceDialect,
    voiceStyle,
    burnSubtitles,
    brollOn,
    avatarKind,
    presetAvatarId,
    avatarProfileId,
    avatar,
  ]);

  /** 「用这份」:把能复原的都铺回去;形象只在**来源可复原**时铺(upload 一律不铺)。 */
  const onUseDraft = () => {
    if (!draft) return;
    setScript(draft.script);
    if (isDhPhotoMode(draft.mode)) setMode(normalizeDhPhotoMode(draft.mode));
    setResolution(draft.resolution);
    setVoiceKind(draft.voiceKind);
    setVoice(draft.voice);
    setProfileId(draft.profileId);
    setSpeechRate(draft.speechRate);
    setPerformanceStyle(draft.performanceStyle);
    // 口播线:「换音色」的选择与逐句 b-roll 标记也要回来 —— v1 不存它们,
    // 恢复出来的稿会把用户选好的声纹静默换回原声(见 DhDraft 字段注释)。
    setUseOriginalVoice(draft.useOriginalVoice);
    // 声音 V2 三件套:loadDhDraft 已按词表收口(老草稿/下架词退默认),这里直接铺。
    setVoiceLanguage(draft.voiceLanguage);
    setVoiceDialect(draft.voiceDialect);
    setVoiceStyle(draft.voiceStyle);
    setBurnSubtitles(draft.burnSubtitles ?? true);
    setBrollOn(draft.brollOn);
    if (draft.avatarKind === "preset") {
      setAvatarKind("preset");
      setPresetAvatarId(draft.avatarRef);
    } else if (draft.avatarKind === "profile") {
      setAvatarKind("profile");
      setAvatarProfileId(draft.avatarRef);
    }
    setDraft(null);
    if (draft.hadUpload) {
      toast({
        title: "这稿的文案与参数已恢复",
        description: "上次那张照片是一次性临时上传,链接已过期 —— 形象要重新选或重新传。",
      });
    }
  };

  /**
   * R4 批量出片的候选形象:**这条线真能用的那些**。
   *
   * ★预置形象只在照片线出现(它是照片资产,`DhAvatarPicker` 的 preset tab 就只在
   *   filmType==="photo" 渲染)—— 把它列进真人口播线的批量,选了也出不来。
   * ★上传口不进候选:那是一次性临时 key,而批量的语义是「同一份文案配**不同的人**」,
   *   一次上传只有一个人,没有「批」可言。
   * ★纯音频线没有形象 → 整个功能不出现(不是禁用,是这条线本就没有这回事)。
   */
  const batchCandidates: DhBatchTarget[] = isTts
    ? []
    : [
        ...(filmType === "photo"
          ? presetAvatars.map((a) => ({
              key: `preset:${a.id}`,
              label: a.label ?? a.id,
              thumbnailUrl: a.thumbnailUrl,
              kind: "photo" as const, // 预置形象是照片资产(视频线根本不列它)
              avatarFields: {
                avatarSource: "preset",
                avatarRef: a.id,
                consentAcknowledged: false,
              },
            }))
          : []),
        // ★批量出片的「我的形象」候选列退役(0915 归一):没有扁平形象列表可枚举了。
        //   批量仍可用预置形象;要按人物批量,是另一种形态(选 N 个人物),本批不做。
      ];

  /**
   * 跑一批。请求体除形象外与单条出片**同一份构造**(`voiceFieldsOf` / 下面这段 spread
   * 与 `onSubmit` 逐字同源)—— 批量不另立一套契约,否则两边一漂就是「单条能出、批量出错档」。
   */
  const onRunBatch = async (targets: DhBatchTarget[]) => {
    batchAbortRef.current = false;
    setBatchRunning(true);
    setBatchResult(null);
    const r = await runDhBatch({
      targets,
      base: {
        mode: isVideoAvatar ? "video" : mode,
        ...voiceFieldsOf(),
        ...(burnSubtitles ? {} : { burnSubtitles: false }), // 字幕开关:批量与单条同口径
        ...(isVideoAvatar ? {} : { resolution }),
        ...(!isVideoAvatar && mode === "flagship" ? { performanceStyle } : {}),
        script: script.trim(),
        speechRate,
        ...(sceneKeyRef.current ? { sceneKey: sceneKeyRef.current } : {}),
        ...(brollCount > 0
          ? {
              brollLines: effectiveLines.map((ln) => {
                const on = isBrollOn(ln);
                const m = brollMaterials[ln.text];
                return {
                  text: ln.text,
                  broll: on,
                  ...(on && m?.assetId ? { assetId: m.assetId } : {}),
                  ...(on && m?.packMaterialId ? { packMaterialId: m.packMaterialId } : {}),
                };
              }),
            }
          : {}),
        useOriginalVoice: isVideoAvatar && useOriginalVoice,
        ...(blueprint ? { blueprintId: blueprint.blueprintId } : {}),
        // 批量走过同一道黄标闸(onOpenBatch);到这里口播里的黄标句已全部确认
        ...(hasFlaggedInScript ? { refFactsConfirmed: true } : {}),
        // 背景音乐:批量与单条同一份文案/参数(见头注),配乐选择同理对所有目标形象生效。
        ...(bgmId != null ? { bgmId } : {}),
        ...(customBgmObjectKey ? { customBgmObjectKey } : {}),
        ...(bgmId != null || customBgmObjectKey ? { bgmVolume } : {}),
      },
      submit: digitalHumanService.createTask,
      onProgress: setBatchItems,
      shouldAbort: () => batchAbortRef.current,
    });
    setBatchItems(r.items);
    setBatchResult(r);
    setBatchRunning(false);
    if (r.succeeded > 0) clearDhDraft(draftKey); // 批量出成了同样算「这稿已经变成任务」
    qc.invalidateQueries({ queryKey: ["recent-works", "dh"] });
    toast({
      title: r.succeeded > 0 ? `已提交 ${r.succeeded} 条` : "一条都没提交成功",
      description: r.stoppedForBalance
        ? `余额/配额不足,剩下 ${r.skipped} 条没有提交,也没有扣费。`
        : r.failed > 0
          ? `另有 ${r.failed} 条失败(逐条原因见弹框),已成功的不受影响。`
          : "生成中,在「最近作品」或作品页看进度。",
      variant: r.succeeded > 0 ? "default" : "destructive",
    });
  };

  // 右栏预览用:按形象来源解析所选形象缩图(上传/我的形象/预置)。
  const selectedAvatarUrl =
    avatarKind === "upload"
      ? avatar?.url
      : avatarKind === "profile"
        ? pickedMaterial?.thumbUrl
        : presetAvatars.find((a) => a.id === presetAvatarId)?.thumbnailUrl;
  // R1-a:抽屉收起后,状态条 chip 与 Dock 都靠它回显「选了谁」。上传口没有名字,
  // 就说「已上传」——不编一个文件名当形象名。
  const selectedAvatarLabel =
    avatarKind === "upload"
      ? avatar
        ? "已上传的形象"
        : null
      : avatarKind === "profile"
        ? (pickedMaterial?.label ?? null)
        : (presetAvatars.find((a) => a.id === presetAvatarId)?.label ?? null);

  return (
    <div className="space-y-3 p-1">
      {/* 状态条:页名 + 真步骤条 + 本线能力 + 换一种做法 + 形象 chip。
          ★片型选择器已经不在这里了 —— 它由「你点的是哪张卡」决定(2026-08-07 IA 重构)。 */}
      <DhCreateStatusBar
        backTo="/ai-digital-human"
        title={DH_LINE_META[line].title}
        current={currentLineCard}
        switchOptions={switchOptions}
        showAvatarChip={!isTts}
        avatarLabel={selectedAvatarLabel}
        avatarThumbUrl={selectedAvatarUrl}
        onOpenAvatar={() => setAvatarSheetOpen(true)}
        avatarDone={avatarReady}
        voiceDone={voiceReady}
        scriptDone={scriptReady}
      />

      {/* R5 草稿恢复条:两个明确出口,不静默自动恢复(刷新的意图未必是「接着上次」) */}
      {draft && (
        <DhDraftBar
          ageLabel={draftAgeLabel(draft.savedAt)}
          hadUpload={draft.hadUpload}
          scriptPreview={draft.script.slice(0, 40)}
          onUse={onUseDraft}
          onDiscard={() => {
            clearDhDraft(draftKey);
            setDraft(null);
          }}
        />
      )}

      {/* P-C 蓝图条:爆款情报路由过来时显示(套路名+重新生成/换蓝图/清除) */}
      {blueprint && (
        <>
          {/* BPWB-1(2026-09-03):细条→蓝图工作卡。口播型爆款多有原片留存 —— 播放位让用户
              当场听到照着学的语气节奏;节拍轨把「照它的结构」画出来。 */}
          <BlueprintHeroCard
            formulaName={blueprint.formulaName}
            sourcePlatform={blueprint.sourcePlatform}
            blueprintId={blueprint.blueprintId}
            hasRawVideo={blueprint.hasRawVideo}
            disabled={submitting || scriptGenerating}
            onSwap={() => navigate("/ai-explosive")}
            onClear={clearBlueprint}
            pledge={
              <>
                口播的<b>结构和节奏照参考片</b>;说的话换成你的产品,出镜的是<b>你的数字人形象</b>。
              </>
            }
            structure={(() => {
              const beats = parseBeats(
                typeof blueprint.payload.beats15Raw === "string" ? blueprint.payload.beats15Raw : ""
              );
              if (beats.length === 0) return undefined;
              return (
                <div className="space-y-1.5">
                  <div className="text-xs text-muted-foreground">
                    <b className="text-foreground">口播结构</b> —— 逐句稿在下方文案框,句句可改
                  </div>
                  <div data-testid="dh-blueprint-beats" className="flex flex-wrap gap-1">
                    {beats.map((bt, i) => (
                      <span
                        key={i}
                        className="rounded-md border border-border/60 bg-card px-2 py-1 text-2xs text-muted-foreground"
                      >
                        {bt.start != null && bt.end != null && (
                          <b className="mr-1 font-mono text-[10px] text-primary/80">
                            {bt.start}–{bt.end}s
                          </b>
                        )}
                        {bt.label}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })()}
          />
          {/* 2026-10-10 §3:有逐句 → 逐句对照编辑区(默认 A 原样翻拍);
              C1 进行中 → 占位;无口播 / C1 失败 → 退回「按公式生成脚本」状态卡。 */}
          {linesMode ? (
            <DhBlueprintLinesEditor
              rows={lineRows}
              preset={linePreset}
              productLabel={productLabel}
              generating={scriptGenerating}
              disabled={submitting}
              hints={rewriteHints}
              manualEdited={scriptManualEdited}
              showAddProductLine={!hasProductLine(lineRows)}
              onPreset={onLinePreset}
              onRowMode={onLineRowMode}
              onConfirm={onLineConfirm}
              onPickProduct={() => setAdaptOpen(true)}
              onGenerate={onLinesGenerate}
              onAddProductLine={onAddProductLine}
              onRemoveAdded={onRemoveAddedLine}
              onResync={() => setScriptManualEdited(false)}
            />
          ) : linesStatus === "loading" ? (
            <div
              data-testid="dh-lines-loading"
              className="flex items-center gap-2 rounded-lg border border-border bg-card/60 px-4 py-3 text-2xs text-muted-foreground"
            >
              <Loader2 className="h-4 w-4 animate-spin" />
              正在逐句分析参考片口播(哪句是钩子、哪里有参考片的数字和品牌)…
            </div>
          ) : (
            <div
              data-testid="dh-adapt-step"
              className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card/60 px-4 py-3"
            >
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="text-[13.5px] font-semibold text-foreground">
                    按公式写你的口播
                  </span>
                  <span className="text-2xs text-muted-foreground">
                    结构照参考片,内容是你的产品
                  </span>
                </div>
                <p className="text-2xs text-muted-foreground">
                  {scriptGenerating
                    ? "正在按「口播公式 × 你的产品事实」生成…"
                    : linesStatus === "failed"
                      ? "参考片口播的逐句分析暂时没拿到 —— 可以重试,或先按公式 × 你的产品事实生成,逐句可改。"
                      : "参考片无口播(纯画面梗)—— 点右侧按公式 × 你的产品事实生成,逐句可改。"}
                </p>
              </div>
              {linesStatus === "failed" && (
                <Button
                  type="button"
                  variant="outline"
                  data-testid="dh-lines-retry"
                  disabled={submitting || scriptGenerating}
                  onClick={() => void loadBlueprintLines(blueprint)}
                >
                  重试逐句分析
                </Button>
              )}
              <Button
                type="button"
                data-testid="dh-adapt-cta"
                disabled={submitting || scriptGenerating}
                onClick={() => setAdaptOpen(true)}
              >
                {scriptGenerating ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="mr-1.5 h-4 w-4" />
                )}
                按公式生成脚本
              </Button>
            </div>
          )}
          {/* 2026-08-13:结构层(公式/节拍/槽位)—— 「引擎真读到的那段」 */}
          <HandoffPreview payload={blueprint.payload as FormulaPayload} />
          {/* 「参考片在拍什么」:2026-10-10 起只读折叠(这块从来不上送,可编辑=骗用户,P4) */}
          {dhReferenceContent && (
            <ReferenceContentPanel
              content={dhReferenceContent}
              original={dhReferenceContent}
              readOnly
            />
          )}
          <DhAdaptProductDialog
            open={adaptOpen}
            onOpenChange={setAdaptOpen}
            variant={linesMode ? "lines" : "formula"}
            generating={scriptGenerating}
            scriptDirty={
              !linesMode &&
              script.trim().length > 0 &&
              script !== (scriptFromBlueprintRef.current ?? "")
            }
            onGenerate={(facts, name) =>
              void (
                linesMode ? onLinesProductPicked(facts, name) : generateScript(blueprint, facts)
              ).then((ok) => ok && setAdaptOpen(false))
            }
          />
          {/* §3.2 黄标确认框(替换 BPWB-1「一字未改」确认框):列出未确认的黄标句。 */}
          <AlertDialog
            open={refFactsConfirm !== null}
            onOpenChange={(v) => !v && setRefFactsConfirm(null)}
          >
            <AlertDialogContent
              data-testid="dh-reffacts-confirm"
              className="max-h-[85dvh] overflow-y-auto"
            >
              <AlertDialogHeader>
                <AlertDialogTitle>这几句里有参考片的事实</AlertDialogTitle>
                <AlertDialogDescription>
                  这几句里的数字/故事/品牌来自参考片,发出去就是你在说。确认可用再出片。
                </AlertDialogDescription>
              </AlertDialogHeader>
              <ul data-testid="dh-reffacts-list" className="space-y-1.5">
                {pendingRefFacts.map((r) => (
                  <li
                    key={r.idx}
                    className="rounded-md border border-amber-300/70 bg-amber-50 px-2.5 py-1.5 text-xs leading-relaxed text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"
                  >
                    {rowText(r)}
                  </li>
                ))}
              </ul>
              <AlertDialogFooter>
                <AlertDialogCancel>回去改</AlertDialogCancel>
                <AlertDialogAction data-testid="dh-reffacts-proceed" onClick={onRefFactsConfirmed}>
                  确认可用
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}

      {/* ── Stage | Dock:与详情页同一副骨架(2026-08-07 R1-c)──────────────────
          ★高度只看视口,不跟兄弟节点抢:13rem ≈ 顶栏 56 + main padding 48 + 状态条 44 + gap。
            蓝图条是条件渲染的 —— 用固定公式,带蓝图的那次不会把工作区压矮一截。
          ★min-h 兜底:矮屏宁可让页面滚,也不把文案框压没。 */}
      <div className="lg:h-[calc(100dvh-13rem)] lg:min-h-[30rem]">
        <div className="grid h-full min-h-0 grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_380px]">
          {/* ── Stage:口播文案是这一页唯一需要长时间停留的控件,给它主角位 ──
              改前它在第 2.1~2.3 屏(y=1904 / y=2504),被形象网格顶下去。 */}
          {/* ★步骤序显式化(0813 婷婷「建议按 上传视频→口播文案→出片 上下排版」):
              宽屏不推翻 Stage|Dock(文案主位是 R1-c 的有据拍板),但块标序号;
              窄屏单列用 max-lg:contents + order 真按序上下排 ——
              改前单列时文案(②)排在形象(①)前面。纯音频线没有形象步,不标。
              2026-09-08 声音 V2:声音从「③出片设置」拆成独立「③声音与演绎」,
              出片设置降为 ④ —— 现在是 ①形象→②文案→③声音→④出片设置。 */}
          <Card
            data-testid="dh-create-stage"
            data-dh-step={isTts ? undefined : 2}
            className="flex min-h-0 max-lg:order-2 flex-col shadow-sm"
          >
            <CardContent className="flex min-h-0 flex-1 flex-col gap-3 p-4">
              <div className="flex shrink-0 items-baseline justify-between gap-2">
                {/* ★序号在 Label 之外:label 文本是 29 个测试与读屏的稳定读者,
                    「② 口播文案」会把它们全打坏(字段第一次有值打坏下游读者的老坑) */}
                {!isTts && (
                  <span data-testid="dh-step-script" aria-hidden className="text-sm font-medium">
                    ②
                  </span>
                )}
                <Label htmlFor="dh-script" className="mr-auto text-sm font-medium">
                  口播文案
                  {blueprint && (
                    <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                      {linesMode
                        ? scriptManualEdited
                          ? "· 最终口播(已手改)"
                          : "· 最终口播(逐句区改动实时拼进来,也可直接改)"
                        : "· 蓝图口播公式已套你的产品,可改"}
                    </span>
                  )}
                </Label>
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                  {script.length}/{scriptMax} 字
                  {(isVideoAvatar || isTts) && ` · 约 ${estSecondsFromChars(script.length)} 秒`}
                </span>
              </div>
              <Textarea
                id="dh-script"
                value={script}
                maxLength={scriptMax}
                disabled={scriptGenerating}
                placeholder={
                  scriptGenerating
                    ? "正在按「口播公式 × 你的产品事实」生成口播脚本…"
                    : isVideoAvatar
                      ? `写一段新台词(≤${videoScriptMax} 字,约 ${videoMaxLabel}),分身会保留原片画面照着念。`
                      : !isTts && mode === "flagship"
                        ? `写一句卖点 / 开场白 / 活动通知(≤${FLAGSHIP_SCRIPT_MAX} 字,约 ${FLAGSHIP_MAX_SEC} 秒),分身会照着念。`
                        : "写一句卖点 / 开场白 / 活动通知,分身会照着念。"
                }
                onChange={(e) => {
                  const v = e.target.value;
                  setScript(v);
                  setTranslateUnverified([]);
                  // 逐句对照态:用户直接改大框 → 逐句区不再回写(与拼出来的一致则视为没改)
                  if (linesMode) setScriptManualEdited(v !== composeScript(lineRows));
                }}
                className="min-h-[8rem] flex-1 resize-none text-sm leading-relaxed"
              />
              {isVideoAvatar && script.trim().length > videoScriptMax && (
                <p className="shrink-0 text-xs text-destructive">
                  视频档文案最长 {videoScriptMax} 字(约 {videoMaxLabel}),请精简后再出片
                </p>
              )}
              {scriptNeedsTranslate && (
                <div
                  data-testid="dh-script-translate-hint"
                  className="flex shrink-0 flex-wrap items-center gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs"
                >
                  <span className="text-foreground">
                    你选了{languageZhName(voiceLanguage)},但稿子是中文 ——
                    分身只会用这个语言的口音念,不会自动翻译,出来还是中文。
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7"
                    disabled={translating}
                    onClick={runTranslate}
                    data-testid="dh-script-translate"
                  >
                    {translating ? "翻译中…" : `一键翻译成${languageZhName(voiceLanguage)}`}
                  </Button>
                </div>
              )}
              {translatedFrom != null && !scriptNeedsTranslate && (
                <div
                  data-testid="dh-script-translated"
                  className="flex shrink-0 flex-wrap items-center gap-2 text-xs text-muted-foreground"
                >
                  <span>已翻译成{languageZhName(voiceLanguage)},请过一遍再出片。</span>
                  <button
                    type="button"
                    className="text-primary underline-offset-2 hover:underline"
                    onClick={restoreTranslated}
                  >
                    还原中文稿
                  </button>
                  {translateUnverified.length > 0 && (
                    <span className="text-warning">
                      这些数字在译文里没对上,请核对:{translateUnverified.join("、")}
                    </span>
                  )}
                </div>
              )}
              {flagshipTooLong && (
                <p className="shrink-0 text-xs text-destructive">
                  生动版最长 {FLAGSHIP_MAX_SEC} 秒(约 {FLAGSHIP_SCRIPT_MAX} 字),现在
                  {script.trim().length} 字。精简一下再出片。
                </p>
              )}

              {/* P-C 蓝图逐句:出镜/b-roll 徽章 + 配图。改前它在右栏 320 的窄列里,
                  现在跟文案同列 —— 逐句改的就是上面那段文案。 */}
              {/* ── I4 音色试听墙(配音线专属)──────────────────────────────────
                  ★这条线上「选哪个音色」就是**主要决策** —— 它却一直是 Dock 里一个干下拉,
                    而试听(sampleUrl)只有声音库页才有。挑配音不让人听,等于让人闭着眼睛买。
                  ★没有样音的音色**不渲染播放器**(不做假试听)—— 与声音库页同一条规矩。 */}
              {line === "tts" && presetVoiceDtos.length > 0 && (
                <div className="min-h-0 shrink-0 space-y-2 overflow-y-auto rounded-md border bg-muted/20 p-2 lg:max-h-[42%]">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className="text-xs font-medium">选个音色</span>
                    <span className="text-2xs text-muted-foreground">
                      点卡片选中 · 有样音的可试听
                    </span>
                  </div>
                  {/* 用途预设:usage 字段一直在,只是没人用过 */}
                  {presentUsages(presetVoiceDtos).length > 0 && (
                    <div className="flex flex-wrap gap-1" data-testid="dh-voice-usage-chips">
                      <button
                        type="button"
                        onClick={() => setVoiceUsage(null)}
                        className={`rounded-full border px-2 py-0.5 text-2xs transition ${
                          voiceUsage === null
                            ? USAGE_CHIP_ON
                            : "text-muted-foreground hover:bg-muted"
                        }`}
                      >
                        全部
                      </button>
                      {presentUsages(presetVoiceDtos).map((u) => (
                        <button
                          key={u.key}
                          type="button"
                          onClick={() => setVoiceUsage(u.key)}
                          className={`rounded-full border px-2 py-0.5 text-2xs transition ${
                            voiceUsage === u.key
                              ? USAGE_CHIP_ON
                              : "text-muted-foreground hover:bg-muted"
                          }`}
                        >
                          {u.label}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {presetVoiceDtos
                      .filter((v) => !voiceUsage || v.usage === voiceUsage)
                      .map((v) => {
                        const active = voiceKind === "preset" && voice === v.id;
                        return (
                          <div
                            key={v.id}
                            className={`rounded-md border p-2 transition ${
                              active ? "border-primary bg-primary/5 ring-1 ring-primary" : ""
                            }`}
                          >
                            <button
                              type="button"
                              aria-label={`选择音色 ${v.label}`}
                              aria-pressed={active}
                              onClick={() => {
                                setVoiceKind("preset");
                                setVoice(v.id);
                              }}
                              className="w-full text-left"
                            >
                              <div className="truncate text-xs font-medium">{v.label}</div>
                              <div className="text-2xs text-muted-foreground">
                                {v.usage ? (VOICE_USAGE_LABEL[v.usage] ?? "内置") : "内置"}
                              </div>
                            </button>
                            {/* 没样音就没有播放器 —— 不做一个点了不出声的按钮 */}
                            {v.sampleUrl && (
                              // eslint-disable-next-line jsx-a11y/media-has-caption
                              <audio
                                onPlay={pauseOtherAudio}
                                src={v.sampleUrl}
                                controls
                                preload="none"
                                data-testid={`voice-sample-${v.id}`}
                                className="mt-1 h-7 w-full"
                              />
                            )}
                            {v.sampleUrl && <ComplianceAudioBadge className="mt-0.5" />}
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* ── P6 样片墙嵌入(照片线专属;设计稿 §3.2「底部」)─────────────────
                  样片的价值是「照着这个改」,它要在**你正要写文案的那一刻**出现;
                  今天它在独立页 `/showcase`,进了创作页就看不见了。
                  只嵌照片线:样片的 avatarId 是预置形象(照片资产),真人口播线连这个
                  来源 tab 都没有。 */}
              {line === "photo" && (
                <DhShowcaseStrip
                  currentScript={script}
                  onApply={(r) => {
                    setScript(r.script);
                    setAvatarKind("preset");
                    setPresetAvatarId(r.avatarId);
                    setVoiceKind("preset");
                    setVoice(r.voiceId);
                    setResolution(r.resolution);
                    setUseOriginalVoice(false);
                    toast({
                      title: "已套用这份样片",
                      description: "形象 / 音色 / 文案 / 清晰度都填好了,改成你自己的再出片。",
                    });
                  }}
                  onViewAll={() => navigate("/ai-digital-human/showcase")}
                />
              )}

              {/* ── I3 照片档单价(照片线专属)──────────────────────────────────
                  1019 照片省流版永久下架后只剩生动版一档,原「两档对照」收成一张单价卡:
                  每秒单价 + 「这稿预计」照常同屏(问后端报价口,见 DhPhotoRateCard 头注)。 */}
              {line === "photo" && (
                <div data-testid="dh-mode-compare" className="shrink-0">
                  <DhPhotoRateCard
                    name="生动版"
                    hint="口型与微表情更活,最长 20 秒"
                    variantKey={dhVariantOf("flagship", resolution, dhVariants)}
                    seconds={cost.seconds}
                  />
                </div>
              )}

              {/* ── 逐句条(I2:从爆款蓝图态解放)───────────────────────────────
                  只在口播成片这条线出现:插播产品画面的前提是「分身在出镜」,
                  纯音频没画面可切,照片档单图合成也谈不上插播。 */}
              {line === "talking" && effectiveLines.length > 0 && (
                <div className="min-h-0 shrink-0 space-y-2 overflow-y-auto rounded-md border bg-muted/20 p-2 lg:max-h-[38%]">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-xs font-medium">逐句 · 谁出镜 / 哪句切产品画面</span>
                    <span className="text-2xs text-muted-foreground">
                      {effectiveLines.length} 句
                    </span>
                  </div>
                  <ul className="space-y-1.5">
                    {effectiveLines.map((ln, i) => {
                      const on = isBrollOn(ln);
                      const m = brollMaterials[ln.text];
                      return (
                        <li key={`${i}-${ln.text}`} className="flex items-start gap-2 text-xs">
                          <span className="mt-0.5 h-4 w-4 shrink-0 rounded-full bg-muted text-center font-mono leading-4 text-muted-foreground">
                            {i + 1}
                          </span>
                          <span className="min-w-0 flex-1">{ln.text}</span>
                          {on && m?.thumbUrl && (
                            <img
                              src={m.thumbUrl as string}
                              alt={`第 ${i + 1} 句配图`}
                              className="h-8 w-8 shrink-0 rounded border border-primary/40 object-cover"
                            />
                          )}
                          {on && (
                            <button
                              type="button"
                              onClick={() => setPickingLine(ln.text)}
                              data-testid={`broll-pick-${i}`}
                              className="shrink-0 rounded border border-primary/40 px-1.5 py-0.5 text-2xs text-primary hover:bg-primary/10"
                            >
                              {m ? "换图" : "配图"}
                            </button>
                          )}
                          {/* 出镜 / 切画面 是这一句的**开关**,不再是蓝图给的只读标 */}
                          <button
                            type="button"
                            aria-pressed={on}
                            aria-label={`第 ${i + 1} 句${on ? "改为出镜" : "改为切产品画面"}`}
                            data-testid={`broll-toggle-${i}`}
                            onClick={() => setBrollOn((prev) => ({ ...prev, [ln.text]: !on }))}
                            className={
                              on
                                ? "shrink-0 rounded border border-primary/40 bg-primary/10 px-1.5 py-0.5 text-2xs text-primary"
                                : "shrink-0 rounded border border-border bg-muted px-1.5 py-0.5 text-2xs text-muted-foreground hover:border-primary/40 hover:text-primary"
                            }
                          >
                            {on ? "切画面" : "出镜"}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  <p className="text-2xs leading-relaxed text-muted-foreground">
                    {brollCount > 0
                      ? `已给 ${brollCount} 句配了产品画面,出片时这几句会切到画面上(其余句分身出镜)。`
                      : "点某句的「出镜」把它改成切产品画面,再配一张图 —— 出片时会真的切过去;不配图的句子保持分身出镜。"}
                    {scriptFromBlueprintRef.current !== null &&
                      script !== scriptFromBlueprintRef.current &&
                      " 你已手动改稿,以上方编辑框内容为准。"}
                  </p>
                </div>
              )}
              {/* ── I3 公共形象前置(照片线专属)────────────────────────────────
                  20 款平台主播在 hub 卡墙下面有一排,可**进了创建页就没了** ——
                  得开弹框、再切到「预置形象」tab 才见。而这条线本来就是冲它来的。 */}
              {line === "photo" && presetAvatars.length > 0 && (
                <div className="shrink-0 space-y-1.5">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-xs font-medium">公共素材</span>
                    <span className="text-2xs text-muted-foreground">
                      平台策展 · 免上传免授权 · 不占克隆配额
                    </span>
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-1" data-testid="dh-preset-strip">
                    {presetAvatars.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        aria-label={`选择 ${a.label}`}
                        onClick={() => {
                          setAvatarKind("preset");
                          setPresetAvatarId(a.id);
                        }}
                        className={`w-20 shrink-0 overflow-hidden rounded-lg border transition hover:-translate-y-0.5 hover:border-primary/55 ${
                          avatarKind === "preset" && presetAvatarId === a.id
                            ? "border-primary ring-2 ring-primary"
                            : ""
                        }`}
                      >
                        <img
                          src={a.thumbnailUrl}
                          alt={a.label}
                          loading="lazy"
                          className="aspect-[3/4] w-full object-cover object-top"
                        />
                        <span className="block truncate px-1 py-1 text-2xs">{a.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ── Dock:形象预览 + 出片设置 + 出片(焊底)────────────────────────── */}
          <div data-testid="dh-create-dock" className="flex min-h-0 max-lg:contents flex-col gap-3">
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1 max-lg:contents">
              {/* 形象预览:弹框关掉后,这里是「这条口播长什么样」的唯一去处。
                  ★纯音频线整张不渲染 —— 页名「配音工作台」+ 副标题已经说过两遍不出画面,
                    再摆一个「只出声音不出画面」的空框就是第三遍,还占着 Dock 最值钱的位置。 */}
              {!isTts && (
                <Card data-dh-step={1} className="max-lg:order-1 shadow-sm">
                  <CardContent className="space-y-2 p-3">
                    <div data-testid="dh-step-avatar" className="text-sm font-medium">
                      ① 选形象
                      <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                        {isVideoAvatar ? "· 上传一段真人视频" : "· 上传照片或选一个"}
                      </span>
                    </div>
                    {/* ★0908 老板「选择声音在最下面,要滑动才能看到」:已选态从 aspect-square
                        全宽大图(右列≈400px 高)压成设计稿的缩略横条(≈64px)——被挤出视口的
                        正是 ③声音与演绎。预览「这条口播长什么样」的诉求保留:64px 缩略图
                        (视频=首帧)点开 Sheet 看大图;未选态的「门」也保留,只降为横向门。 */}
                    {selectedAvatarUrl ? (
                      <button
                        type="button"
                        data-testid="dh-pick-avatar"
                        onClick={() => setAvatarSheetOpen(true)}
                        className="flex w-full items-center gap-3 rounded-md border p-2 text-left transition hover:border-primary/60 hover:bg-muted/40"
                      >
                        {isVideoAvatar ? (
                          <video
                            src={`${selectedAvatarUrl}#t=0.1`}
                            muted
                            playsInline
                            preload="metadata"
                            className="h-16 w-16 shrink-0 rounded-md border bg-black object-cover"
                          />
                        ) : (
                          <MediaThumb
                            url={selectedAvatarUrl}
                            ratio="square"
                            alt="所选形象"
                            className="h-16 w-16 shrink-0 rounded-md border"
                          />
                        )}
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium">
                            {selectedAvatarLabel ?? "已选形象"}
                          </span>
                          <span className="block text-2xs text-muted-foreground">
                            {isVideoAvatar ? "真人视频 · 点击查看或更换" : "点击查看或更换"}
                          </span>
                        </span>
                      </button>
                    ) : (
                      /* ★选形象是出片的必过门槛 —— 入口仍长成门的样子(历史反馈「按钮很小」),
                         只从正方形改横向门:醒目不变,不再把 ③声音 挤出首屏。 */
                      <button
                        type="button"
                        data-testid="dh-pick-avatar"
                        onClick={() => setAvatarSheetOpen(true)}
                        className="flex h-20 w-full items-center justify-center gap-3 rounded-md border-2 border-dashed border-border/70 text-sm text-muted-foreground transition hover:border-primary/60 hover:bg-muted/40 hover:text-primary"
                      >
                        <UserSquare2 className="h-7 w-7 shrink-0" />
                        <span className="text-left">
                          <span className="block font-medium">选一个形象</span>
                          <span className="block text-2xs">
                            上传真人 / 我的素材 / 预置 / 按人物选
                          </span>
                        </span>
                      </button>
                    )}
                    {/* 已选态的名字/更换已并进压缩条本体;这行只剩未选时的状态提示。 */}
                    {!selectedAvatarLabel && (
                      <div className="text-xs text-muted-foreground">
                        {isTts ? "无形象(纯音频)" : "还没选"}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* ── ③ 声音与演绎(声音系统 V2,2026-09-08 拆卡)────────────────────
                  从「出片设置」拆出来:声音是它自己的一组决策(来源 × 音色 × 语言 × 方言 ×
                  演绎 × 语速 × 试听),塞在档位/清晰度中间就只剩一个干下拉。出片设置降为 ④。
                  ★方言/多语言/演绎只对「我的声纹」真实生效(克隆声线走新模型);预置音色是
                    旧引擎 → C 批返工:选预置时**置灰不消失**(老板「多语言没体现」),
                    一行说明留在顶部,控件点不动但看得见。
                  ★纯音频线沿用无序号形态(与旧「出片设置」同一条规矩):tts 的预置音色主
                    决策在 Stage 的试听墙,这张卡的 chip 墙在 tts 只列声纹/克隆。 */}
              <Card data-dh-step={isTts ? undefined : 3} className="max-lg:order-3 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">
                    <span data-testid={isTts ? undefined : "dh-step-voice"}>
                      {isTts ? "声音与演绎" : "③ 声音与演绎"}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {isVideoAvatar && (
                    <div className="space-y-2 rounded-md border border-dashed p-3">
                      {/* 单选而非勾选:这本就是「二选一」,勾选框表达的是「加个选项」,语义不对。
                      且选「换一个音色」才展开音色控件 = 渐进展开,不是勾一下控件凭空消失。
                      改名:旧标签「用原声」读起来像「保留原视频那段话」,实际是克隆音色念**新文案**。 */}
                      <div role="radiogroup" aria-label="声音" className="space-y-1.5">
                        {[
                          {
                            v: true,
                            label: "用我的原声念新文案",
                            hint: "从源视频里学你的音色,念下面填的新文案",
                          },
                          { v: false, label: "换一个音色", hint: "用预置音色或你保存的声纹来念" },
                        ].map((o) => (
                          <button
                            key={String(o.v)}
                            type="button"
                            role="radio"
                            aria-checked={useOriginalVoice === o.v}
                            aria-label={o.label}
                            onClick={() => setUseOriginalVoice(o.v)}
                            className={`flex w-full items-start gap-2 rounded-md border p-2 text-left text-sm transition ${
                              useOriginalVoice === o.v
                                ? "border-primary bg-primary/5"
                                : "hover:bg-muted/40"
                            }`}
                          >
                            <span
                              className={`mt-0.5 h-3.5 w-3.5 shrink-0 rounded-full border-2 ${
                                useOriginalVoice === o.v
                                  ? "border-primary bg-primary"
                                  : "border-muted-foreground/40"
                              }`}
                            />
                            <span>
                              {o.label}
                              <span className="block text-xs text-muted-foreground">{o.hint}</span>
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* ── 「用谁的声音」chip 墙(C 批返工,2026-09-08 老板「页面没按设计稿/
                      弹框用了系统默认」)。此前是「来源 segment + 预置 Select + 声纹原生
                      select」三层抽屉 —— 声音的主决策被折进两个下拉,原生 select 还长成系统
                      默认样。改成一面墙:我的声纹们 → 预置音色们(带样音的右上小圆钮试听)→
                      恒在末位的「+ 克隆我的声音」虚线卡。voiceKind/voice/profileId 三个
                      state 原名不动,只改交互面(草稿/返稿/批量的读写全都指着它们)。
                      ★配音线(tts)不在这面墙列预置卡:那条线的预置主决策在 Stage 的 I4
                        试听墙(带用途筛),两处同列会出双份同名卡;这面墙在 tts 只补它
                        没有的「我的声纹 / 现场克隆」。两面墙同写 voiceKind/voice,不打架。
                      ★视频档选「用我的原声」时整面墙不渲染(现状逻辑:不涉及选音色)。
                      档位/清晰度/表演风格已随 2026-09-08 拆卡搬去 ④ 出片设置。 */}
                  {!(isVideoAvatar && useOriginalVoice) &&
                    (() => {
                      // ★1006 老板「声纹太多显示不全」:4 列小卡把名字截成三个字,5 条「我的声音」
                      //   截断后分不清。改为两组(我的声纹在前 · 内置音色在后)、窄栏 2 列、
                      //   名字允许换行不截断;声纹副标题带录制日期区分同名;折叠只对内置组。
                      //   选中/锁定逻辑不变(voiceKind/voice/profileId 原名原义)。
                      const wallPresets = isTts
                        ? []
                        : presetVoiceDtos.length > 0
                          ? presetVoiceDtos
                          : // 后端不可达 → 回落写死表(E4 同一条兜底,墙不塌;没 usage/样音)
                            voiceOptions.map((o) => ({
                              id: o.value,
                              label: o.label,
                              usage: null,
                              sampleUrl: null,
                            }));
                      type WallCard = {
                        key: string;
                        id: string;
                        label: string;
                        desc: string;
                        mine: boolean;
                        legacy: boolean;
                        active: boolean;
                        sampleUrl: string | null;
                        aria: string;
                        onPick: () => void;
                      };
                      const profileCards: WallCard[] = voiceProfiles.map((p) => {
                        const date = voiceProfileDateLabel(p.createdAt);
                        return {
                          key: `profile:${p.id}`,
                          id: String(p.id),
                          label: p.label,
                          desc: date ? `我的声纹 · ${date}` : "我的声纹",
                          mine: true,
                          legacy: !voiceProfileSupportsTuning(p),
                          active: voiceKind === "profile" && profileId === String(p.id),
                          // 设计稿声纹卡带试听钮:DTO 有 sampleUrl(原始录音样本)就给;
                          // 后端没透出该字段时落 null → 不渲染钮(不做假试听,仓规)。
                          sampleUrl: (p.sampleUrl ?? null) as string | null,
                          // 同名声纹靠日期区分,读屏也要能分开
                          aria: `选择声纹 ${p.label}${date ? ` · ${date}` : ""}`,
                          onPick: () => {
                            setVoiceKind("profile");
                            setProfileId(String(p.id));
                          },
                        };
                      });
                      const presetCards: WallCard[] = wallPresets.map((v) => ({
                        key: `preset:${v.id}`,
                        id: v.id,
                        label: v.label,
                        desc: v.usage ? (VOICE_USAGE_LABEL[v.usage] ?? "内置") : "内置",
                        mine: false,
                        legacy: false,
                        active: voiceKind === "preset" && voice === v.id,
                        sampleUrl: v.sampleUrl ?? null,
                        aria: `选择音色 ${v.label}`,
                        onPick: () => {
                          setVoiceKind("preset");
                          setVoice(v.id);
                        },
                      }));
                      // 折叠只对内置组;选中卡落在折叠线以下时不收(收了=「选了谁」从屏上消失)。
                      const selIdx = presetCards.findIndex((c) => c.active);
                      const collapsible =
                        presetCards.length > PRESET_WALL_VISIBLE && selIdx < PRESET_WALL_VISIBLE;
                      const collapsed = collapsible && !voiceWallExpanded;
                      const shownPresets = collapsed
                        ? presetCards.slice(0, PRESET_WALL_VISIBLE)
                        : presetCards;
                      const renderCard = (c: WallCard) => (
                        <div
                          key={c.key}
                          data-testid={`dh-voice-wall-card-${c.key}`}
                          className={cn(
                            "relative rounded-md border p-2 transition",
                            c.active
                              ? "border-primary bg-primary/5"
                              : "hover:border-muted-foreground/40 hover:bg-muted/40"
                          )}
                        >
                          <button
                            type="button"
                            aria-pressed={c.active}
                            aria-label={c.aria}
                            onClick={c.onPick}
                            className={cn("w-full text-left", c.sampleUrl && "pr-5")}
                          >
                            {/* 名字完整可读:允许换行,不 truncate(老板同名 5 条全靠看全名+日期) */}
                            <div
                              data-testid="dh-voice-wall-name"
                              className="whitespace-normal break-words text-xs font-medium leading-snug"
                            >
                              {c.label}
                            </div>
                            <div
                              className={cn(
                                "mt-0.5 flex flex-wrap items-center gap-1 text-2xs",
                                c.mine ? "text-primary" : "text-muted-foreground"
                              )}
                            >
                              <span>{c.desc}</span>
                              {c.legacy && <LegacyVoiceBadge />}
                            </div>
                          </button>
                          {/* 没样音就没有播放钮 —— 不做点了不出声的假试听 */}
                          {c.sampleUrl && (
                            <VoiceSampleDot voiceId={c.id} label={c.label} src={c.sampleUrl} />
                          )}
                        </div>
                      );
                      return (
                        <div className="space-y-2" data-testid="dh-voice-wall">
                          <div className="flex items-baseline justify-between gap-2">
                            <Label className="text-xs">用谁的声音</Label>
                            <button
                              type="button"
                              onClick={() => navigate("/ai-digital-human/voices")}
                              className="text-2xs text-primary underline-offset-2 hover:underline"
                            >
                              去声音库管理 →
                            </button>
                          </div>
                          {/* 组一:我的声纹(恒在前、默认全部展开;末位「+ 克隆我的声音」) */}
                          <div className="space-y-1.5" data-testid="dh-voice-wall-group-profiles">
                            <div className="text-2xs font-medium text-muted-foreground">
                              我的声纹{profileCards.length > 0 ? `(${profileCards.length})` : ""}
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              {profileCards.map(renderCard)}
                              {/* testid 沿用 dh-enroll-more —— 读它的测试家族不必换锚。 */}
                              <button
                                type="button"
                                data-testid="dh-enroll-more"
                                aria-pressed={showEnrollCard}
                                onClick={() => setShowEnrollCard((v) => !v)}
                                className={cn(
                                  "rounded-md border border-dashed p-2 text-left transition",
                                  showEnrollCard
                                    ? "border-primary text-primary"
                                    : "border-border/70 text-muted-foreground hover:border-primary/60 hover:text-primary"
                                )}
                              >
                                <div className="text-xs font-medium">+ 克隆我的声音</div>
                                <div className="mt-0.5 text-2xs">
                                  朗读 10 秒以上,录到 19 秒自动停
                                </div>
                              </button>
                            </div>
                          </div>
                          {/* 组二:内置音色(超过 PRESET_WALL_VISIBLE 张折叠) */}
                          {presetCards.length > 0 && (
                            <div className="space-y-1.5" data-testid="dh-voice-wall-group-presets">
                              <div className="text-2xs font-medium text-muted-foreground">
                                内置音色
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                {shownPresets.map(renderCard)}
                              </div>
                              {collapsible && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  data-testid="dh-voice-wall-toggle"
                                  aria-expanded={!collapsed}
                                  onClick={() => setVoiceWallExpanded((v) => !v)}
                                  className="h-7 w-full text-xs"
                                >
                                  {collapsed
                                    ? `展开全部内置音色(共 ${presetCards.length} 款)`
                                    : "收起内置音色"}
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                  {/*
                   * 现场克隆卡(即录即证,B5 二刀):朗读授权文本录 20 秒 → 建成即选中当前音色。
                   * 视频档「用原声」不涉及选音色 → 不渲染。
                   *
                   * ★0813 琳琳:「上传克隆过了,做视频选择声音时还要提示再录一遍」——
                   *   这张卡此前**无条件**摆在这儿:已经有音色的人每次出片都被要求再录一遍,
                   *   看着像「上次那次没成」。C 批 chip 墙后统一为:恒收起,墙末位的
                   *   「+ 克隆我的声音」虚线卡(dh-enroll-more)一点就铺开 —— 零声纹的人
                   *   同样看得见这张虚线卡,主路仍是一步之遥,不再对已克隆过的人唠叨。
                   */}
                  {!(isVideoAvatar && useOriginalVoice) && showEnrollCard && (
                    <VoiceRecordEnrollCard
                      onEnrolled={(p) => {
                        setVoiceKind("profile");
                        setProfileId(String(p.id));
                        setShowEnrollCard(false);
                      }}
                    />
                  )}

                  {/* ── 声音 V2 三件套:语言 / 口音方言 / 演绎方式(2026-09-08)──────────
                      能力规则:只对「我的声纹」真实生效(克隆声线走新模型)。预置音色是旧引擎。
                      ★C 批返工(老板点名「多语言没体现」——被藏起来了):选预置时**不再整体
                        消失**,改「禁用置灰 + 顶部一行说明」:能力常驻可见,用户才知道换到
                        声纹能解锁什么。点不动靠容器 pointer-events-none,Radix Select 再上
                        disabled 双保险(键盘 Tab 也进不去)。dh-voice-v2-locked 语义保留,
                        挂在说明行上。
                      ★0908 二返:视频档「用原声」也放三件套(它就是克隆,ai 侧一直吃这三个
                        字段;此前不放 = 最像真人的那条路被锁死 natural)。原声路只是没有
                        profileId 可试听 → 试听条仍按 profileId 闸,三件套照常可调。 */}
                  {
                    <div className="space-y-2">
                      {!voiceTuningUnlocked && (
                        <p
                          className="text-2xs leading-relaxed text-muted-foreground"
                          data-testid="dh-voice-v2-locked"
                          data-lock-reason={legacyProfileSelected ? "legacy-profile" : "preset"}
                        >
                          {legacyProfileSelected
                            ? LEGACY_PROFILE_LOCK_TEXT
                            : PRESET_VOICE_LOCK_TEXT}
                        </p>
                      )}
                      <div
                        data-testid="dh-voice-v2"
                        aria-disabled={!voiceTuningUnlocked}
                        className={cn(
                          "space-y-4",
                          !voiceTuningUnlocked && "pointer-events-none select-none opacity-50"
                        )}
                      >
                        <div className="space-y-1.5">
                          <Label htmlFor="dh-voice-language" className="text-xs">
                            语言
                          </Label>
                          <Select
                            value={voiceLanguage}
                            onValueChange={onChangeVoiceLanguage}
                            disabled={!voiceTuningUnlocked}
                          >
                            <SelectTrigger
                              id="dh-voice-language"
                              data-testid="dh-voice-language"
                              className="text-sm"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {DH_VOICE_LANGUAGES.map((l) => (
                                <SelectItem key={l.value} value={l.value}>
                                  {l.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-2xs text-muted-foreground">
                            换语言,还是这个人的声音在说;稿子要用这个语言写(不会自动翻译)
                          </p>
                        </div>
                        {/* 口音/方言:中文专属(契约「仅 zh 可非空」;换语言时 onChangeVoiceLanguage
                            已顺手清方言,这里只按语言闸渲染)。高频 5 种平铺,其余收「更多方言」。 */}
                        {voiceLanguage === "zh" && (
                          <div className="space-y-1.5" data-testid="dh-voice-dialect">
                            <Label className="text-xs">口音 / 方言</Label>
                            <div className="flex flex-wrap items-center gap-1.5">
                              {[
                                { value: null as string | null, label: "跟随原声" },
                                ...DH_VOICE_DIALECTS.filter((d) =>
                                  (DH_FEATURED_DIALECTS as readonly string[]).includes(d.value)
                                ),
                              ].map((d) => {
                                const active = voiceDialect === d.value;
                                return (
                                  <button
                                    key={d.value ?? "original"}
                                    type="button"
                                    aria-pressed={active}
                                    data-testid={`dh-dialect-${d.value ?? "original"}`}
                                    onClick={() => setVoiceDialect(d.value)}
                                    className={cn(
                                      "rounded-full border px-3 py-1 text-xs transition-colors",
                                      active
                                        ? "border-primary bg-primary/12 font-medium text-primary"
                                        : "border-border/60 text-muted-foreground hover:border-primary/40 hover:text-foreground"
                                    )}
                                  >
                                    {d.label}
                                  </button>
                                );
                              })}
                              {/* 其余 15 种收进 shadcn Select(C 批返工:老板点名「弹框用了
                                  系统默认」—— 原生 select 的下拉面板长成操作系统样,换 Radix
                                  弹层;触发器保持 chip 圆角小形制)。
                                  ★Radix SelectItem 不收空串 value:「回普通话」用哨兵值
                                    __original__ 映射回 null(=跟随原声);根 value="" 时显示 placeholder。 */}
                              {(() => {
                                const more = DH_VOICE_DIALECTS.filter(
                                  (d) =>
                                    !(DH_FEATURED_DIALECTS as readonly string[]).includes(d.value)
                                );
                                const moreActive =
                                  voiceDialect != null &&
                                  more.some((d) => d.value === voiceDialect);
                                return (
                                  <Select
                                    value={moreActive ? voiceDialect : ""}
                                    onValueChange={(v) =>
                                      setVoiceDialect(v === "__original__" ? null : v)
                                    }
                                    disabled={!voiceTuningUnlocked}
                                  >
                                    <SelectTrigger
                                      aria-label="更多方言"
                                      data-testid="dh-dialect-more"
                                      className={cn(
                                        "h-[26px] w-auto gap-1 rounded-full px-2 py-0 text-xs transition-colors focus:ring-1 focus:ring-offset-0",
                                        moreActive
                                          ? "border-primary font-medium text-primary"
                                          : "border-border/60 text-muted-foreground"
                                      )}
                                    >
                                      <SelectValue placeholder="更多方言…" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="__original__">回跟随原声</SelectItem>
                                      {more.map((d) => (
                                        <SelectItem key={d.value} value={d.value}>
                                          {d.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                );
                              })()}
                            </div>
                          </div>
                        )}
                        <div className="space-y-1.5" data-testid="dh-voice-style">
                          <Label className="text-xs">演绎方式</Label>
                          <div
                            role="radiogroup"
                            aria-label="演绎方式"
                            className="grid grid-cols-3 gap-2"
                          >
                            {DH_VOICE_STYLES.map((s) => {
                              const active = voiceStyle === s.value;
                              return (
                                <button
                                  key={s.value}
                                  type="button"
                                  role="radio"
                                  aria-checked={active}
                                  aria-label={s.label}
                                  data-testid={`dh-voice-style-${s.value}`}
                                  onClick={() => setVoiceStyle(s.value)}
                                  className={cn(
                                    "rounded-md border p-2 text-left transition",
                                    active
                                      ? "border-primary bg-primary/5"
                                      : "hover:border-muted-foreground/40 hover:bg-muted/40"
                                  )}
                                >
                                  <div className="text-xs font-medium">{s.label}</div>
                                  <div className="mt-0.5 text-2xs text-muted-foreground">
                                    {s.desc}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                          <p className="text-2xs text-muted-foreground">
                            断句、情绪和播讲口吻由声音导演自动安排。{VOICE_STYLE_TRADEOFF_TEXT}
                          </p>
                        </div>
                      </div>
                    </div>
                  }

                  {/* B5 语速分档(2026-09-08 老板「设 1.2 实际没快」= 连续滑杆拖不准 + 1.1 太subtle)。
                      离散档点得准、快/很快明显。所有出片路径都是 script TTS → 恒显示。 */}
                  <div className="space-y-1.5" data-testid="dh-speech-rate">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">语速</Label>
                      <span className="text-xs tabular-nums text-muted-foreground">
                        {speechRate}x
                      </span>
                    </div>
                    <div role="radiogroup" aria-label="语速" className="flex flex-wrap gap-1.5">
                      {SPEECH_RATE_STEPS.map((s) => {
                        const active = nearestSpeechStep(speechRate) === s.value;
                        return (
                          <button
                            key={s.value}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            aria-label={`语速 ${s.value} 倍速`}
                            data-testid={`dh-speech-rate-${s.value}`}
                            onClick={() => setSpeechRate(s.value)}
                            className={cn(
                              "cursor-pointer whitespace-nowrap rounded-full border px-3 py-1 text-xs transition-colors",
                              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-background",
                              active
                                ? "border-primary bg-primary/12 font-medium text-primary"
                                : "border-border/60 text-muted-foreground hover:border-primary/40 hover:text-foreground"
                            )}
                          >
                            {s.label}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-2xs text-muted-foreground">
                      分档更容易点准;「快 / 很快」比默认明显。只作用于本次合成,不改音色资产。
                    </p>
                  </div>

                  {/* 组合试听条(C 批返工,设计稿形制):圆形播放钮 + 两行组合说明 + 播放器,
                      替代原来的小 outline 按钮。按当前 声纹 × 语言 × 方言 × 演绎 × 语速 现合成
                      一小段听感;B 批端点,失败 toast 不阻断出片。
                      ★常驻渲染、仅选中声纹时可点:选预置时随三件套一起置灰(同一条「能力常驻
                        可见」的返工口径);声纹路但还没点具体哪条时同样置灰。 */}
                  {!(isVideoAvatar && useOriginalVoice) &&
                    (() => {
                      const previewable = voiceKind === "profile" && !!profileId;
                      const previewDesc = `${
                        previewable
                          ? (voiceProfiles.find((p) => String(p.id) === profileId)?.label ??
                            "我的声纹")
                          : "先在上面选一条我的声纹"
                      } · ${
                        DH_VOICE_LANGUAGES.find((l) => l.value === voiceLanguage)?.label ??
                        voiceLanguage
                      }${
                        voiceLanguage === "zh" ? ` · ${dialectLabelOf(voiceDialect)}` : ""
                      } · ${voiceStyleLabelOf(voiceStyle)} —— 念口播文案前 30 字`;
                      return (
                        <div
                          data-testid="dh-voice-preview"
                          aria-disabled={!previewable}
                          className={cn(
                            "flex flex-wrap items-center gap-3 rounded-md border border-primary/40 bg-primary/5 p-3",
                            !previewable && "pointer-events-none select-none opacity-50"
                          )}
                        >
                          <button
                            type="button"
                            data-testid="dh-voice-preview-btn"
                            aria-label="试听这套声音"
                            disabled={previewingVoice || !previewable}
                            onClick={() => void previewVoiceCombo()}
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition hover:bg-primary/90 disabled:opacity-70"
                          >
                            {previewingVoice ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Play className="ml-0.5 h-4 w-4" />
                            )}
                          </button>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium">试听这套声音</div>
                            <div className="truncate text-2xs text-muted-foreground">
                              {previewDesc}
                            </div>
                          </div>
                          {voicePreviewUrl && (
                            // eslint-disable-next-line jsx-a11y/media-has-caption
                            <audio
                              onPlay={pauseOtherAudio}
                              src={voicePreviewUrl}
                              controls
                              autoPlay
                              data-testid="dh-voice-preview-audio"
                              className="h-8 w-44 shrink-0 max-sm:w-full"
                            />
                          )}
                          {voicePreviewUrl && <ComplianceAudioBadge />}
                        </div>
                      );
                    })()}
                </CardContent>
              </Card>

              {/* ── ④ 出片设置(声音拆去 ③ 后剩下的画面参数:档位/清晰度/表演风格/BGM)──
                  ★纯音频线整张不渲染:tts 没有档位/清晰度/表演风格/BGM 里的任何一样 ——
                    不是禁用,是这档本就没有这些设置(与预置形象/批量同一条规矩)。 */}
              {!isTts && (
                <Card data-dh-step={4} className="max-lg:order-4 shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">
                      <span data-testid="dh-step-settings">④ 出片设置</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {isVideoAvatar && (
                      <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
                        画面沿用源视频,清晰度跟随源视频;{VIDEO_REVERSAL_HINT}(最长约 {videoMaxLabel}
                        )。
                      </p>
                    )}
                    {!isVideoAvatar && (
                      // 1019 照片省流版永久下架:照片档只剩生动版,不再给「档位」下拉(单选项下拉没意义)。
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div className="space-y-1.5">
                          <Label htmlFor="dh-resolution" className="text-xs">
                            清晰度
                          </Label>
                          <Select value={resolution} onValueChange={setResolution}>
                            {/* 无 aria-label:让 htmlFor 提供名、SelectValue 提供值,读屏念得出当前档;
                                testid 给自动化一个稳锚(Radix trigger 是 combobox,不是原生 select)。 */}
                            <SelectTrigger
                              id="dh-resolution"
                              data-testid="dh-resolution"
                              className="text-sm"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {DH_RESOLUTION_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value}>
                                  {o.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {/*
                           * ★0812 雷:「上传照片清晰度下降」。
                           *   照片档的成片分辨率**由这一档决定,不跟随原照片** —— 而默认是标清 480P。
                           *   用户传一张清晰的照片、什么都没改,拿回来就是 480P,于是理解成
                           *   「系统把我的照片压了」。
                           * ★没有把默认改成 720P:默认清晰度是产品口径决定,不是这条反馈能顺手带的
                           *   (写这条时两档还是 500 / 900 积分每秒的 ×10 老价;现价生动版各清晰度
                           *   都固定 168 积分/秒 —— 0831 价表 omnihuman,用户 0926 拍板 —— 成本论据
                           *   已不成立,但「默认档谁定」这一点没变)。
                           *   能做的是**在他做选择的那一刻把话说清**。
                           */}
                          <p
                            data-testid="dh-resolution-hint"
                            className="text-2xs leading-relaxed text-muted-foreground"
                          >
                            {/* ★1005 生产实测:「高清 720」档竖版照片实出 1024×1366,旧句「选 720 档出来也是 720」
                                与实际不符。火山 OmniHuman 的 output_resolution 是「清晰度档」——宽高比跟随照片、
                                像素由引擎按档决定(0823 实测 3:4 照片:1080 档 1248×1664,720 档 832×1120 / 1024×1366)。 */}
                            成片清晰度按这一档出,
                            <b className="font-medium text-foreground">不跟随你照片本身的分辨率</b>
                            ;720 / 1080
                            是画质档位而非固定像素:画面比例跟随照片,具体像素由引擎按档位决定(如竖版照片「高清
                            720」常见约 1024×1366),「超清 1080」更清晰。
                          </p>
                        </div>
                        {DH_PERFORMANCE_STYLE_ENABLED && mode === "flagship" && (
                          <div className="space-y-1.5">
                            <Label className="text-xs">表演风格</Label>
                            <div
                              className="flex rounded-md border p-0.5"
                              data-testid="dh-performance-style"
                            >
                              {DH_PERFORMANCE_STYLE_OPTIONS.map((o) => (
                                <button
                                  key={o.value}
                                  type="button"
                                  onClick={() => setPerformanceStyle(o.value)}
                                  className={`flex-1 rounded px-2 py-1.5 text-xs transition ${
                                    performanceStyle === o.value
                                      ? "bg-primary text-primary-foreground"
                                      : "text-muted-foreground hover:bg-muted"
                                  }`}
                                >
                                  {o.label}
                                </button>
                              ))}
                            </div>
                            <p className="text-2xs leading-relaxed text-muted-foreground">
                              控制人物怎么演(眼神/手势/动作幅度);说什么仍由口播文案决定。
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                    {/* 字幕开关(2026-09-08 老板「让用户选择是否把字幕烧上去」):默认开
                        (0830 起的默认字幕行为);关掉出干净画面(发布平台自动字幕党用)。
                        tts 无画面 → 整张 ④ 卡本就不渲染,无需另闸。 */}
                    <div
                      className="flex items-center justify-between"
                      data-testid="dh-subtitle-toggle"
                    >
                      <div className="space-y-0.5">
                        <Label htmlFor="dh-burn-subtitles" className="text-xs">
                          字幕
                        </Label>
                        <p className="text-2xs leading-relaxed text-muted-foreground">
                          {burnSubtitles
                            ? "成片自动烧录字幕,与口播逐句对齐"
                            : "不烧字幕,出干净画面(适合发布平台自动加字幕)"}
                        </p>
                      </div>
                      <Switch
                        id="dh-burn-subtitles"
                        aria-label="烧录字幕"
                        checked={burnSubtitles}
                        onCheckedChange={setBurnSubtitles}
                      />
                    </div>
                    {/* 背景音乐(2026-09-07):只在出视频的档位显示 —— 纯配音档(tts)产物是
                        音频,不混 BGM(整张 ④ 卡在 tts 下都不渲染,同一条规矩)。 */}
                    <DhBgmPanel
                      bgms={bgms}
                      bgmTab={bgmTab}
                      onBgmTabChange={setBgmTab}
                      bgmId={bgmId}
                      onSelectPresetBgm={handleSelectPresetBgm}
                      customBgmObjectKey={customBgmObjectKey}
                      customBgmFile={customBgmFile}
                      customBgmUrl={customBgmUrl}
                      uploadingBgm={uploadingBgm}
                      onClearCustomBgm={handleClearCustomBgm}
                      onCustomBgmUpload={handleCustomBgmUpload}
                      bgmVolume={bgmVolume}
                      onBgmVolumeChange={setBgmVolume}
                    />
                  </CardContent>
                </Card>
              )}

              {error && <p className="text-sm text-destructive max-lg:order-4">{error}</p>}
            </div>

            {/* ── 出片驾驶舱(焊底)──────────────────────────────────────────
                与视觉舱「出图驾驶舱焊底」同一条规矩:主按钮不随设置区滚走。
                改前它靠右栏整体 sticky 顶到 top-4,设置一多就得先滚回去才够得着。 */}
            <div
              data-testid="dh-dock-actions"
              className="shrink-0 space-y-2 rounded-lg border bg-card/80 p-3 backdrop-blur max-lg:order-5"
            >
              <div className="flex items-baseline justify-between text-xs text-muted-foreground">
                <span>
                  {isTts
                    ? "纯音频(mp3)· 按秒计费"
                    : `清晰度 · ${isVideoAvatar ? "跟随源视频" : dhResolutionLabel(resolution)}`}
                </span>
                <span>语速 {speechRate.toFixed(1)}x</span>
              </div>
              {/* ★I5:写文案的时候其实就是在花钱,别等详情页才知道花了多少。
                  ★★必须写「预计」——单价与卡面同源、字数→秒数与后端同源,但成片实际时长
                    由合成结果定,说死了就是在骗人(视频台本图报 280 实扣 700 的教训)。 */}
              <div
                data-testid="dh-cost-estimate"
                className="flex items-baseline justify-between text-xs"
              >
                <span className="text-muted-foreground">
                  {cost.seconds > 0 && dhQuotedCredits != null
                    ? `预计 ≈ ${dhQuotedCredits.toLocaleString()} 积分`
                    : "预计积分 · 写了文案才算得出"}
                </span>
                {/* 算式同样不再乘 /80(三皮肤实测 3.17~3.67):它是**给人核对的算式**。 */}
                {/* B2 #16 / 0927 K4 I:单价读目录不取整列(3.5),不用取整上界(4);取不到显「—」 */}
                {cost.seconds > 0 && (
                  <span className="text-muted-foreground" data-testid="dh-cost-formula">
                    {script.trim().length} 字 ≈ {cost.seconds} 秒 ×{" "}
                    {dhPerSecondRate == null ? "—" : formatPerSecondRate(dhPerSecondRate)}/秒
                  </span>
                )}
              </div>
              {/* ★可访问名钉死(Z9d 0926):主按钮与下面「批量出片」的名字都含「出片」,
                  Playwright `getByRole("button", { name: /出片/ })` 撞 strict mode。主按钮保留
                  「出片(消耗积分)」原名(4 条单测 + e2e `/^出片/` 钉着),批量那颗改用不含「出片」
                  的 aria-label —— 可见文案两颗都不动。 */}
              <Button
                className="w-full"
                disabled={!canSubmit}
                onClick={onSubmit}
                aria-label="出片(消耗积分)"
              >
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                出片(消耗积分)
              </Button>
              {/* ── R4 批量出片(次要动作,排在主按钮之下)───────────────────────
                  ★门只看**文案**,不看形象:批量的形象在弹框里另选,这里已经选的那位
                    与批量无关。要求「先选形象」才让点,等于逼用户为一个用不上的选择走一遍流程。
                  ★纯音频线不出现这个按钮 —— 那条线没有形象,不是禁用是本就没有这回事。 */}
              {!isTts && batchCandidates.length > 0 && (
                <Button
                  variant="outline"
                  className="w-full"
                  data-testid="dh-batch-open"
                  /* 可访问名不含「出片」(见主按钮注释):可见文案仍是「批量出片(一份文案 × 多个形象)」 */
                  aria-label="批量生成(一份文案 × 多个形象)"
                  disabled={!scriptReady || submitting || scriptGenerating || uploadingBgm}
                  onClick={onOpenBatch}
                >
                  批量出片(一份文案 × 多个形象)
                </Button>
              )}
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate("/ai-digital-human")}
              >
                取消
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ── 这条线的最近作品(IA 设计稿 §3.1/3.2/3.3「底部」)────────────────────
          ★放在 Stage|Dock **之外、之下**:上面那块是「出这一条片」要用的全部东西,
            R1 把它压进 1.00 屏就是为了不用滚就能出片。这条带是**回看**,天生该在首屏之下 ——
            它不参与出片,滚下去才看得到正合适。
          ★按 mode 筛而不是 sceneKey:sceneKey 只有从场景卡进来的任务才有(见 WorkItem.variant
            的注释),拿它筛会把用户真做出来的片大半筛没。 */}
      <RecentWorksRow
        lines={["dh"]}
        filter={recentFilter}
        title={`${DH_LINE_META[line].title} · 最近作品`}
        onOpen={(w) => navigate(w.detailRoute)}
        onViewAll={(href) => navigate(href)}
      />

      {/* 形象选择弹框(2026-08-08 由抽屉改回弹框,与全仓选择器一致;见组件头注) */}
      <DhAvatarPicker
        open={avatarSheetOpen}
        onOpenChange={setAvatarSheetOpen}
        onPicked={() => setAvatarSheetOpen(false)}
        filmType={filmType}
        avatarKind={avatarKind}
        setAvatarKind={setAvatarKind}
        allowPhoto={allowPhoto}
        consent={consent}
        setConsent={setConsent}
        uploading={uploading}
        avatar={avatar}
        pendingVideo={pendingVideo}
        onPickAvatar={onPickAvatar}
        uploadError={avatarError}
        onSaveUploadedToProfiles={() => void saveUploadedToProfiles()}
        savingToProfiles={savingToProfiles}
        profilesLoading={false /* 形象列表已退役,无加载态 */}
        visibleProfiles={visibleProfiles}
        avatarProfileId={avatarProfileId}
        setAvatarProfileId={setAvatarProfileId}
        presetsLoading={presetsQuery.isLoading}
        presetAvatars={presetAvatars}
        presetAvatarId={presetAvatarId}
        setPresetAvatarId={setPresetAvatarId}
        pickCharacter={pickCharacter}
        // 0916:形象库已并入人物库,直接指过去(旧路由只是个重定向,别再绕一跳)。
        onGoAvatarLibrary={() => navigate("/characters")}
        maxVideoMb={MAX_VIDEO_MB}
        maxVideoDurationLabel={dhSourceDurationLabel(videoLimits.maxSourceSec)}
      />

      {/* R4 批量出片弹框:选 N 位形象 → 串行提交 → 逐条在原地亮(不跳走,因为要跳的有 N 个) */}
      <DhBatchDialog
        open={batchOpen}
        onOpenChange={setBatchOpen}
        candidates={batchCandidates}
        perItemCredits={dhQuotedCredits}
        seconds={cost.seconds}
        running={batchRunning}
        items={batchItems}
        result={batchResult}
        onRun={(t) => void onRunBatch(t)}
        onStop={() => {
          batchAbortRef.current = true;
        }}
        onViewAll={() => navigate("/works?line=dh")}
      />

      <BrollMaterialPicker
        open={pickingLine !== null}
        onOpenChange={(v) => !v && setPickingLine(null)}
        onPick={(m) => {
          if (pickingLine !== null) {
            setBrollMaterials((prev) => ({ ...prev, [pickingLine]: m }));
          }
          setPickingLine(null);
        }}
      />
    </div>
  );
}
